import { initStandalonePromptHelpers, createPromptRefs, isLinkedBraceToken, promptRefsMatchingPrompt } from '../utils/standalonePromptHelpers.js';
import { validateRequiredFields, addOptionIfNotExist } from '../utils/formHelpers.js';
import { setupCharacterCount, updateCount } from '../utils/validation.js';
import { moveDefaultToFront, dedupeById } from '../../../core/utils/index.js';
import { normalizeGenderForFilter } from '../../utils/genderUtils.js';
import { showToast } from '../../utils/toast.js';
import { getBlockedNsfwPromptMessage, getBlockedNsfwScenarioTagMessage, scenarioIsNsfw } from '../../utils/nsfwTags.js';
import { getBlockedUnrecognizedPromptMessage } from '../danbooruTags.js';
import { getNsfwEnabled } from '../../utils/userPrefs.js';
import { showSuccess, showError, showWarning } from '../../utils/UnifiedNotificationSystem.js';
import { storage } from '../../utils/storage.js';
import { storageManager } from '../../../core/storage/index.js';
import { savePreview, loadPreview, deletePreview, cleanupAndSavePreview } from '../../utils/previewCache.js';
import { HangoutService } from '../../hangout/services/HangoutService.js';
import apiClient from '../../../services/ApiClient.js';
import { CREATE_WORKER_URL, USER_WORKER_URL, WORKER_URL, GENERATION_API_URL, isSfwSite } from '../../config.js';
import { applyCreditsRemaining, generateBackgroundPreview, getBase64FromUrl, showLoginRequired, getStoredQuota, resumePendingGeneration, generateImage, setLoadingState, setPreviewLoadingState } from '../imageGeneration.js';
import { CREATION_FLAT_CREDITS, buildLocalCreditResult, computeStandaloneImageCost, formatCreditsUsageLine, loadCreditPricing } from '../creditCostApi.js';
import { pickStandingActionId } from '../../utils/spriteFallbacks.js';
import { CDN_URL, catalogJsonUrl } from '../../config.js';
import { fetchCache } from '../../utils/fetchCache.js';
import { initSearchDropdown } from '../ui/searchDropdown.js';
import { initOAuthManager } from '../../utils/oauthManager.js';
import { attachCreateVideoRankedCharacterPickerToTrigger } from '../ui/rankedCharacterPickerModal.js';
import { attachCreateLocationPickerToTrigger } from '../ui/locationPickerModal.js';
import { addSwipeSupport } from '../../../core/utils/swipe-handlers.js';
import { getBackgroundService } from '../../../core/services/BackgroundService.js';
import { applyTranslations, getCurrentLanguage as getTranslationLanguage, getByPath, interpolate } from '../../translations.js';
import { sfwModerationMessage } from '../sfwModerationError.js';
import { getScenarioTags, setScenarioTags } from '../tags/scenarioTags.js';
import { mapCreateCharacters } from '../utils/mapCreateCharacters.js';
import { unwrapOutfitsPayload } from '../utils/unwrapOutfitsPayload.js';
import { fetchPrivateCharacters } from '../../utils/privateCharactersApi.js';
import { getCurrentUserId } from '../../utils/auth_utils.js';
import { compareNumericCharacterIds } from '../../utils/characterIdSort.js';
import {
    fetchOwnedScenariosForEdit,
    fetchOwnedScenarioForEdit,
} from '../../utils/ownedItemsApi.js';
import { mountOwnedItemEditPicker } from '../ui/ownedItemEditPicker.js';
import { bindMediaCropPanel } from '../ui/mediaCropPanel.js';
import {
    BANNER_CROP_OUTPUT_PX,
    BANNER_OUTPUT_MAX_KB,
    MAX_BANNER_OUTPUT_BYTES,
    MAX_SOURCE_UPLOAD_BYTES,
    SOURCE_MAX_MB,
    getDataUrlDecodedByteLength,
    isAllowedBannerUploadType,
} from '../mediaUploadLimits.js';
import {
    expandCharTokensToNames,
    tokenizeScenarioPayload
} from '../../utils/character-name-tokens.js';


const REQUIRED_FIELDS = ['adventure-title', 'scenario-description', 'generate-bg-1'];
let bannerUploadSource = 'prompt';

const hasBannerPreviewMedia = () => {
    const preview = document.getElementById('banner-image-preview');
    if (!preview) return false;
    if (preview.querySelector('img, video')) return true;
    const bg = preview.style.backgroundImage || '';
    return bg.includes('url(');
};

/** Step index (0–4) for each field that can fail validation. Used to navigate user to first error step. */
const FIELD_TO_STEP = {
    'adventure-title': 1,
    'scenario-description': 1,
    'scenario-tag-search': 1,
    'adventure-banner-prompt': 4,
    'generate-bg-1': 2
};

const getStepForFieldId = (fieldId) => {
    if (fieldId in FIELD_TO_STEP) return FIELD_TO_STEP[fieldId];
    if (fieldId && (fieldId.startsWith('crew-char-') || fieldId.startsWith('crew-role-') || fieldId.startsWith('message-') || fieldId.startsWith('char-status-') || fieldId.startsWith('greeting-intent-'))) return 3;
    return 1;
};
const TEXT_FIELDS = [
    { id: 'adventure-title', counter: 'title-counter', max: 50 },
    { id: 'scenario-description', counter: 'scenario-description-counter', max: 750 },
    { id: 'adventure-banner-prompt', counter: 'banner-prompt-counter', max: 250 }
];

const refreshScenarioCounters = () => {
    TEXT_FIELDS.forEach(({ id, counter, max }) => {
        updateCount(document.getElementById(id), document.getElementById(counter), max);
    });
    refreshCastCounters();
};

const DRAFT_KEY = 'scenario_draft_v2';
const GOAL_MAX = 200;
const GREETING_INTENT_MAX = 450;
const GREETING_INTENT_VARIATION_MAX = 5;
const bannerPromptRefs = createPromptRefs();

let ownedScenarioEditId = null;
let greetingIntentLockedCount = 0;

let isReorderingCastSlots = false;
let isApplyingCastCharacterChange = false;

const getVisibleCharacterCount = () => Math.min(3, Math.max(1, parseInt(document.getElementById('num-characters')?.value || '1', 10) || 1));

const bindLiveCounter = (input, maxLength) => {
    if (!input) return;
    input.removeAttribute('maxlength');
    let counter = input.nextElementSibling;
    if (!counter?.classList?.contains('character-counter')) {
        counter = document.createElement('div');
        counter.className = 'character-counter';
        input.insertAdjacentElement('afterend', counter);
    }
    updateCount(input, counter, maxLength);
    if (input.dataset.liveCountBound === '1') return;
    input.dataset.liveCountBound = '1';
    input.addEventListener('input', () => {
        const next = input.nextElementSibling;
        if (!next?.classList?.contains('character-counter')) return;
        updateCount(input, next, maxLength);
    });
};

const refreshCastCounters = () => {
    for (let slot = 1; slot <= 3; slot += 1) {
        bindLiveCounter(document.getElementById(`message-${slot}`), GOAL_MAX);
    }
    document.querySelectorAll('.greeting-intent-input').forEach((input) => {
        bindLiveCounter(input, GREETING_INTENT_MAX);
    });
};

const getScenarioStepLengthError = (stepIndex) => {
    if (stepIndex === 1) {
        const value = document.getElementById('scenario-description')?.value || '';
        const maxLength = 750;
        if (value.length > maxLength) {
            return `Scenario Description field is too long (${value.length}/${maxLength} characters). Please shorten it.`;
        }
        return null;
    }
    if (stepIndex !== 3) return null;
    const count = getVisibleCharacterCount();
    for (let i = 1; i <= count; i += 1) {
        const goal = document.getElementById(`message-${i}`)?.value || '';
        if (goal.length > GOAL_MAX) {
            return `Character ${i} goal must be ${GOAL_MAX} characters or less.`;
        }
    }
    const rows = Array.from(getGreetingIntentGrid()?.querySelectorAll('.greeting-intent-row') || []);
    for (const row of rows) {
        for (let slot = 1; slot <= count; slot += 1) {
            const input = row.querySelector(`.greeting-intent-input[data-slot="${slot}"]`);
            if ((input?.value || '').length > GREETING_INTENT_MAX) {
                return `Greeting intent must be ${GREETING_INTENT_MAX} characters or less.`;
            }
        }
    }
    return null;
};

const getGreetingIntentGrid = () => document.getElementById('greeting-intents-grid');

const collectGreetingIntentsBySlot = (numChars) => {
    const count = numChars || getVisibleCharacterCount();
    const rows = Array.from(getGreetingIntentGrid()?.querySelectorAll('.greeting-intent-row') || []);
    const bySlot = Array.from({ length: count }, () => []);
    for (const row of rows) {
        const values = [];
        for (let slot = 1; slot <= count; slot += 1) {
            const input = row.querySelector(`.greeting-intent-input[data-slot="${slot}"]`);
            values.push((input?.value || '').trim());
        }
        const filledCount = values.filter(Boolean).length;
        if (filledCount === 0) continue;
        if (filledCount !== count) {
            return { error: 'Each greeting variation must be filled for every character, or left empty for all.', bySlot: null };
        }
        if (values.some((value) => value.length > GREETING_INTENT_MAX)) {
            return { error: `Greeting intent must be ${GREETING_INTENT_MAX} characters or less.`, bySlot: null };
        }
        values.forEach((value, index) => bySlot[index].push(value));
    }
    if (bySlot.some((intents) => intents.length !== bySlot[0].length)) {
        return { error: 'Greeting variations must match across characters.', bySlot: null };
    }
    return { error: null, bySlot };
};

const syncGreetingIntentSlotVisibility = () => {
    const num = getVisibleCharacterCount();
    document.querySelectorAll('.greeting-intent-cell').forEach((cell) => {
        const slot = parseInt(cell.getAttribute('data-slot') || '0', 10);
        const shouldShow = slot <= num;
        cell.classList.toggle('hidden', !shouldShow);
    });
};

const GREETING_INTENT_LABEL_FALLBACK = 'Greeting intent {n}';

const resolveGreetingIntentLabel = (n) => {
    const lang = getTranslationLanguage() || 'en';
    const pageT = window.translations?.[lang]?.['create-scenario'] || {};
    const enT = window.translations?.en?.['create-scenario'] || {};
    const template = getByPath(pageT, 'steps.cast.greeting_intent_label')
        || getByPath(enT, 'steps.cast.greeting_intent_label')
        || GREETING_INTENT_LABEL_FALLBACK;
    return interpolate(String(template), { n });
};

const reindexGreetingIntentRows = () => {
    const rows = Array.from(getGreetingIntentGrid()?.querySelectorAll('.greeting-intent-row') || []);
    rows.forEach((row, index) => {
        const n = index + 1;
        row.dataset.variation = String(index);
        row.querySelectorAll('.greeting-intent-input').forEach((input) => {
            input.dataset.variation = String(index);
        });
        const removeBtn = row.querySelector('.greeting-intent-remove');
        if (removeBtn) removeBtn.hidden = index === 0;
        const labelText = resolveGreetingIntentLabel(n);
        row.querySelectorAll('[data-translate="steps.cast.greeting_intent_label"]').forEach((label) => {
            label.setAttribute('data-n', String(n));
            label.textContent = labelText;
        });
        const labels = row.querySelectorAll('.greeting-intent-variation-label');
        labels.forEach((label) => {
            label.textContent = index === 0 ? '' : `Variation ${index + 1}`;
        });
    });
};

const handleRemoveGreetingIntentRow = (row) => {
    const grid = getGreetingIntentGrid();
    if (!grid || !row || row.dataset.locked === 'true') return;
    if (grid.querySelectorAll('.greeting-intent-row').length <= 1) return;
    if (Number(row.dataset.variation) < greetingIntentLockedCount) return;
    row.remove();
    reindexGreetingIntentRows();
};

const addGreetingIntentRow = (presetValues = []) => {
    const grid = getGreetingIntentGrid();
    if (!grid) return null;
    const existingCount = grid.querySelectorAll('.greeting-intent-row').length;
    if (existingCount >= GREETING_INTENT_VARIATION_MAX) return null;
    const variation = existingCount;
    const row = document.createElement('div');
    row.className = 'greeting-intent-row';
    row.dataset.variation = String(variation);
    const num = 3;
    for (let slot = 1; slot <= num; slot += 1) {
        const cell = document.createElement('div');
        cell.className = `greeting-intent-cell${slot > getVisibleCharacterCount() ? ' hidden' : ''}`;
        cell.dataset.slot = String(slot);
        const label = document.createElement('label');
        label.className = 'form-field';
        const span = document.createElement('span');
        span.className = 'form-label';
        span.setAttribute('data-translate', 'steps.cast.greeting_intent_label');
        span.setAttribute('data-n', String(variation + 1));
        span.textContent = resolveGreetingIntentLabel(variation + 1);
        const textarea = document.createElement('textarea');
        textarea.className = 'form-textarea greeting-intent-input';
        textarea.dataset.slot = String(slot);
        textarea.dataset.variation = String(variation);
        textarea.rows = 1;
        textarea.setAttribute('data-translate-placeholder', 'steps.cast.placeholder_greeting_intent');
        textarea.placeholder = 'How should this character start the conversation?';
        textarea.value = presetValues[slot - 1] || '';
        label.appendChild(span);
        label.appendChild(textarea);
        bindLiveCounter(textarea, GREETING_INTENT_MAX);
        cell.appendChild(label);
        row.appendChild(cell);
    }
    if (variation > 0 && variation >= greetingIntentLockedCount) {
        const removeBtn = document.createElement('button');
        removeBtn.type = 'button';
        removeBtn.className = 'greeting-intent-remove';
        removeBtn.setAttribute('aria-label', 'Remove greeting variation');
        removeBtn.textContent = '×';
        removeBtn.addEventListener('click', () => handleRemoveGreetingIntentRow(row));
        row.appendChild(removeBtn);
    }
    grid.appendChild(row);
    syncGreetingIntentSlotVisibility();
    applyTranslations('create-scenario', getTranslationLanguage());
    reindexGreetingIntentRows();
    if (typeof window.refreshScenarioGreetingWands === 'function') {
        window.refreshScenarioGreetingWands();
    }
    return row;
};

const setGreetingIntentsFromDraft = (intentsBySlot) => {
    const grid = getGreetingIntentGrid();
    if (!grid) return;
    grid.innerHTML = '';
    const slotArrays = [1, 2, 3].map((slot) => {
        const raw = intentsBySlot?.[slot];
        if (Array.isArray(raw)) return raw.map((value) => String(value || ''));
        if (typeof raw === 'string' && raw.trim()) return [raw.trim()];
        return [];
    });
    const rowCount = Math.max(1, ...slotArrays.map((arr) => arr.length));
    for (let i = 0; i < Math.min(rowCount, GREETING_INTENT_VARIATION_MAX); i += 1) {
        addGreetingIntentRow([slotArrays[0][i] || '', slotArrays[1][i] || '', slotArrays[2][i] || '']);
    }
};

const lockExistingGreetingIntentRows = () => {
    const rows = Array.from(getGreetingIntentGrid()?.querySelectorAll('.greeting-intent-row') || []);
    greetingIntentLockedCount = rows.length;
    rows.forEach((row) => {
        row.dataset.locked = 'true';
        row.querySelector('.greeting-intent-remove')?.remove();
    });
};

const setNumCharactersLocked = (locked) => {
    const sel = document.getElementById('num-characters');
    if (sel) sel.disabled = locked;
    sel?.closest('.form-field')?.classList.toggle('is-hidden', locked);
    const searchRoot = document.querySelector('[data-search-root="num-characters"]');
    if (searchRoot) {
        searchRoot.classList.toggle('is-disabled', locked);
        searchRoot.style.pointerEvents = locked ? 'none' : '';
    }
    const trigger = document.getElementById('num-characters-search-trigger');
    if (trigger) {
        trigger.setAttribute('aria-disabled', locked ? 'true' : 'false');
        trigger.tabIndex = locked ? -1 : 0;
        trigger.style.pointerEvents = locked ? 'none' : '';
    }
};

const setScenarioSubmitLabel = (isUpdate) => {
    const label = document.querySelector('button[type="submit"] [data-translate="steps.banner.create_btn"]')
        || document.querySelector('button.create-character-btn span');
    if (!label) return;
    const lang = getTranslationLanguage();
    const t = window.translations?.[lang]?.['create-scenario'] ?? window.translations?.en?.['create-scenario'] ?? {};
    label.textContent = isUpdate
        ? (getByPath(t, 'steps.banner.update_btn') || 'Update Scenario')
        : (getByPath(t, 'steps.banner.create_btn') || 'Create Scenario');
    refreshScenarioCreditInfo(isUpdate);
};

const refreshScenarioCreditInfo = (isUpdate = Boolean(ownedScenarioEditId)) => {
    const creditInfo = document.getElementById('scenario-credit-info');
    if (!creditInfo) return null;
    if (isSfwSite() || isUpdate) {
        creditInfo.classList.add('is-hidden');
        return null;
    }
    const result = buildLocalCreditResult(CREATION_FLAT_CREDITS);
    const text = creditInfo.querySelector('span');
    if (text) text.textContent = formatCreditsUsageLine({ total: CREATION_FLAT_CREDITS, available: result.available_credits });
    creditInfo.classList.remove('is-hidden');
    creditInfo.classList.toggle('insufficient', result.has_enough_credits === false);
    return result;
};

const clearOwnedScenarioEditMode = () => {
    ownedScenarioEditId = null;
    greetingIntentLockedCount = 0;
    setNumCharactersLocked(false);
    setScenarioSubmitLabel(false);
};

const applyOwnedScenarioToForm = async (item) => {
    ownedScenarioEditId = item.scenario_id || item.id;
    greetingIntentLockedCount = 0;
    isRestoringScenarioDraft = true;
    const characters = Array.isArray(item.characters) ? [...item.characters].sort((a, b) => Number(a.position) - Number(b.position)) : [];
    const count = Math.min(3, Math.max(1, characters.length || 1));
    const namesBySlot = {};

    try {
        const titleEl = document.getElementById('adventure-title');
    const descrEl = document.getElementById('scenario-description');
    const bannerEl = document.getElementById('adventure-banner-prompt');
    if (titleEl) titleEl.value = item.name || '';
    if (descrEl) descrEl.value = item.descr || '';
    if (bannerEl) bannerEl.value = item.banner_prompt || '';

    const visibilityHidden = document.getElementById('scenario-visibility');
    const publicBtn = document.getElementById('public-btn');
    const privateBtn = document.getElementById('private-btn');
    const isPriv = Boolean(item.is_private);
    if (visibilityHidden) visibilityHidden.value = isPriv ? 'private' : 'public';
    if (publicBtn && privateBtn) {
        publicBtn.classList.toggle('active', !isPriv);
        privateBtn.classList.toggle('active', isPriv);
        publicBtn.setAttribute('aria-pressed', String(!isPriv));
        privateBtn.setAttribute('aria-pressed', String(isPriv));
    }

    if (Array.isArray(item.tags)) setScenarioTags(item.tags);

    const numSel = document.getElementById('num-characters');
    if (numSel) {
        numSel.value = String(count);
        numSel.dispatchEvent(new Event('change', { bubbles: true }));
    }
    setNumCharactersLocked(true);

    const intentsBySlot = {};
    for (const char of characters) {
        const slot = Number(char.position) || 1;
        let charName = String(char.name || char.character_name || '').trim();
        if (!charName) {
            try {
                const details = await HangoutService.getInstance().getCharacterDetails(char.character_id);
                charName = details?.name || '';
            } catch {
                charName = '';
            }
        }
        namesBySlot[slot] = charName;
        setCrewCharacterSlot(slot, { id: char.character_id, name: charName || `Character ${slot}` }, { dispatchChange: true });
        const role = document.getElementById(`crew-role-${slot}`);
        if (role && char.outfit_id != null) {
            addOptionIfNotExist(role, String(char.outfit_id), `Outfit ${char.outfit_id}`);
            role.value = String(char.outfit_id);
            role.dispatchEvent(new Event('change', { bubbles: true }));
        }
        const messageField = document.getElementById(`message-${slot}`);
        if (messageField) messageField.value = expandCharTokensToNames(char.goal || '', namesBySlot);
        intentsBySlot[slot] = Array.isArray(char.greeting_intents)
            ? char.greeting_intents.map((intent) => expandCharTokensToNames(intent || '', namesBySlot))
            : [];
        await updateRoleDropdown(slot, { forceDefault: false });
        if (role && char.outfit_id != null) {
            role.value = String(char.outfit_id);
            role.dispatchEvent(new Event('change', { bubbles: true }));
        }
    }
    setGreetingIntentsFromDraft(intentsBySlot);
    lockExistingGreetingIntentRows();

    const bgSel = document.getElementById('generate-bg-1');
    if (bgSel && item.location_id != null) {
        addOptionIfNotExist(bgSel, String(item.location_id), `Location ${item.location_id}`);
        bgSel.value = String(item.location_id);
        bgSel.dispatchEvent(new Event('change', { bubbles: true }));
    }
    const timeEl = document.getElementById('time-of-day-1');
    if (timeEl && item.time) {
        timeEl.value = `0.${item.time}`;
        timeEl.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const bannerPreview = document.getElementById('banner-image-preview');
    if (bannerPreview && ownedScenarioEditId) {
        setBg(bannerPreview, `${CDN_URL}/data/scenarios/images/${ownedScenarioEditId}.webp`);
    }

    if (titleEl) titleEl.value = expandCharTokensToNames(titleEl.value || '', namesBySlot);
    if (descrEl) descrEl.value = expandCharTokensToNames(descrEl.value || '', namesBySlot);
    if (bannerEl) bannerEl.value = expandCharTokensToNames(bannerEl.value || '', namesBySlot);
    rewriteStoryFields((value) => expandCharTokensToNames(value, namesBySlot));

    setScenarioSubmitLabel(true);
    window.scenarioWizard?.completeStep(0);
    window.scenarioWizard?.setStep(1);
    } finally {
        rebuildIdentitiesFromSlotsFirstFree();
        refreshScenarioCounters();
        isRestoringScenarioDraft = false;
    }
};

const getCastSlotState = (slot) => {
    const charSel = document.getElementById(`crew-char-${slot}`);
    const roleSel = document.getElementById(`crew-role-${slot}`);
    const goalEl = document.getElementById(`message-${slot}`);
    const previewEl = document.getElementById(`character-${slot}-preview`);
    return {
        charValue: charSel?.value || '',
        charText: charSel?.options?.[charSel.selectedIndex]?.textContent || '',
        roleValue: roleSel?.value || '',
        roleText: roleSel?.options?.[roleSel.selectedIndex]?.textContent || '',
        roleHtml: roleSel?.innerHTML || '',
        goal: goalEl?.value || '',
        previewHtml: previewEl?.innerHTML || '',
        greetingIntents: Array.from(getGreetingIntentGrid()?.querySelectorAll(`.greeting-intent-input[data-slot="${slot}"]`) || []).map((input) => input.value)
    };
};

const previewSeqBySlot = [0, 0, 0, 0];
const castPreviewActionByTuple = new Map();
let isApplyingAiCastBatch = false;
let isRestoringScenarioDraft = false;

const CHAR_IDENTITY_LETTERS = ['A', 'B', 'C'];
const CHAR_STORY_FIELD_IDS = ['adventure-title', 'scenario-description', 'adventure-banner-prompt'];
const identityByCharId = {};
const lastCrewBySlot = {
    1: { id: '', name: '' },
    2: { id: '', name: '' },
    3: { id: '', name: '' }
};

const tokenForLetter = (letter) => `{char${letter}}`;

const escapeRegExp = (value) => String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const getFirstFreeIdentityLetter = () => {
    const used = new Set(Object.values(identityByCharId));
    return CHAR_IDENTITY_LETTERS.find((letter) => !used.has(letter)) || null;
};

const getCharIdentitySnapshot = () => CHAR_IDENTITY_LETTERS.map((letter) => {
    const charId = Object.keys(identityByCharId).find((id) => identityByCharId[id] === letter) || '';
    let name = '';
    if (charId) {
        for (let slot = 1; slot <= 3; slot += 1) {
            const sel = document.getElementById(`crew-char-${slot}`);
            if (sel && String(sel.value) === String(charId)) {
                name = sel.options?.[sel.selectedIndex]?.textContent?.trim() || '';
                break;
            }
        }
    }
    return {
        letter,
        token: tokenForLetter(letter),
        id: charId,
        name: name || tokenForLetter(letter),
        empty: !charId
    };
});

const rewriteStoryFields = (rewriter) => {
    CHAR_STORY_FIELD_IDS.forEach((id) => {
        const el = document.getElementById(id);
        if (!el) return;
        el.value = rewriter(el.value || '');
    });
    for (let slot = 1; slot <= 3; slot += 1) {
        const goalEl = document.getElementById(`message-${slot}`);
        if (goalEl) goalEl.value = rewriter(goalEl.value || '');
    }
    getGreetingIntentGrid()?.querySelectorAll('.greeting-intent-input').forEach((input) => {
        input.value = rewriter(input.value || '');
    });
    refreshScenarioCounters();
};

const replaceNameWithTokenInText = (text, name, token) => {
    if (!text || !name || !token) return text;
    const pattern = new RegExp(`(?<![\\p{L}\\p{N}_])${escapeRegExp(name)}(?![\\p{L}\\p{N}_])`, 'giu');
    return String(text).replace(pattern, token);
};

const replaceTokenWithNameInText = (text, letter, name) => {
    if (!text || !letter || !name) return text;
    return String(text).replace(new RegExp(`\\{\\{?char${letter}\\}\\}?`, 'gi'), name);
};

const rewriteAiCharTokensToNames = (text, namesByOrigSlot) => {
    let out = String(text || '');
    const letters = { 1: 'A', 2: 'B', 3: 'C' };
    for (let n = 3; n >= 1; n -= 1) {
        const name = namesByOrigSlot[n];
        if (!name) continue;
        out = out.replace(new RegExp(`\\{\\{?char${n}\\}\\}?`, 'gi'), name);
        out = out.replace(new RegExp(`\\{\\{?char${letters[n]}\\}\\}?`, 'gi'), name);
    }
    if (namesByOrigSlot[1]) {
        out = out.replace(/\{\{char\}\}(?![A-C0-9])/gi, namesByOrigSlot[1]);
        out = out.replace(/\{char\}(?![A-C0-9])/gi, namesByOrigSlot[1]);
    }
    return out;
};

const applyAiCharTokensToNames = (namesByOrigSlot) => {
    rewriteStoryFields((value) => rewriteAiCharTokensToNames(value, namesByOrigSlot));
};

const resolveFilledIdentityTokensInForm = () => {
    getCharIdentitySnapshot().forEach((row) => {
        if (row.empty || !row.name || row.name === row.token) return;
        rewriteStoryFields((value) => replaceTokenWithNameInText(value, row.letter, row.name));
    });
};

const getVisibleCrewIdSet = () => {
    const ids = new Set();
    const num = getVisibleCharacterCount();
    for (let slot = 1; slot <= num; slot += 1) {
        const charId = document.getElementById(`crew-char-${slot}`)?.value;
        if (charId) ids.add(String(charId));
    }
    return ids;
};

const pruneIdentitiesToVisibleSlots = () => {
    const visibleIds = getVisibleCrewIdSet();
    Object.keys(identityByCharId).forEach((id) => {
        if (!visibleIds.has(String(id))) delete identityByCharId[id];
    });
};

const clearHiddenCrewSlots = () => {
    const num = getVisibleCharacterCount();
    const visibleIds = getVisibleCrewIdSet();
    for (let slot = num + 1; slot <= 3; slot += 1) {
        const sel = document.getElementById(`crew-char-${slot}`);
        if (!sel) {
            lastCrewBySlot[slot] = { id: '', name: '' };
            continue;
        }
        const charId = sel.value ? String(sel.value) : '';
        const name = sel.options?.[sel.selectedIndex]?.textContent?.trim() || '';
        if (charId && !visibleIds.has(charId)) {
            const letter = identityByCharId[charId];
            if (letter && name) {
                rewriteStoryFields((value) => replaceNameWithTokenInText(value, name, tokenForLetter(letter)));
            }
            delete identityByCharId[charId];
        }
        if (charId) {
            sel.value = '';
            syncSearchDropdownLabel(sel.id);
        }
        lastCrewBySlot[slot] = { id: '', name: '' };
    }
    pruneIdentitiesToVisibleSlots();
};

const refreshLastCrewBySlot = () => {
    for (let slot = 1; slot <= 3; slot += 1) {
        const sel = document.getElementById(`crew-char-${slot}`);
        lastCrewBySlot[slot] = {
            id: sel?.value || '',
            name: sel?.options?.[sel.selectedIndex]?.textContent?.trim() || ''
        };
    }
};

const rebuildIdentitiesFromSlotsFirstFree = () => {
    Object.keys(identityByCharId).forEach((key) => delete identityByCharId[key]);
    const num = getVisibleCharacterCount();
    for (let slot = 1; slot <= num; slot += 1) {
        const sel = document.getElementById(`crew-char-${slot}`);
        const charId = sel?.value || '';
        if (!charId) continue;
        const letter = getFirstFreeIdentityLetter();
        if (letter) identityByCharId[String(charId)] = letter;
    }
    clearHiddenCrewSlots();
    refreshLastCrewBySlot();
};

const handleCrewIdentityChange = (previousId, previousName, nextId, nextName) => {
    if (isReorderingCastSlots || isApplyingAiCastBatch || isRestoringScenarioDraft || isClearingScenarioDraft || isResettingScenarioUI) return;
    const prevId = previousId ? String(previousId) : '';
    const newId = nextId ? String(nextId) : '';
    if (prevId && newId && prevId !== newId) {
        const inheritedLetter = identityByCharId[prevId] || identityByCharId[newId] || getFirstFreeIdentityLetter();
        if (inheritedLetter && previousName) {
            rewriteStoryFields((value) => replaceNameWithTokenInText(value, previousName, tokenForLetter(inheritedLetter)));
        }
        delete identityByCharId[prevId];
        if (inheritedLetter) identityByCharId[newId] = inheritedLetter;
        if (inheritedLetter && nextName) {
            rewriteStoryFields((value) => replaceTokenWithNameInText(value, inheritedLetter, nextName));
        }
    } else if (prevId && prevId !== newId) {
        const letter = identityByCharId[prevId];
        if (letter) {
            rewriteStoryFields((value) => replaceNameWithTokenInText(value, previousName, tokenForLetter(letter)));
            delete identityByCharId[prevId];
        }
    } else if (newId && prevId !== newId) {
        let letter = identityByCharId[newId];
        if (!letter) {
            letter = getFirstFreeIdentityLetter();
            if (letter) identityByCharId[newId] = letter;
        }
        if (letter && nextName) {
            rewriteStoryFields((value) => replaceTokenWithNameInText(value, letter, nextName));
        }
    }
    clearHiddenCrewSlots();
};

const syncSearchDropdownLabel = (selectId) => {
    const selectEl = document.getElementById(selectId);
    if (!selectEl) return;
    const registry = window.searchDropdownRegistry?.[selectId];
    if (registry?.refresh) {
        registry.refresh();
        return;
    }
    const trigger = document.getElementById(`${selectId}-search-trigger`);
    const label = trigger?.querySelector('.search-dropdown__label');
    if (!label) return;
    const selected = selectEl.options?.[selectEl.selectedIndex];
    label.textContent = selected?.textContent?.trim() || '';
};

const setCrewCharacterSlot = (slot, { id, name } = {}, { dispatchChange = true } = {}) => {
    const charSel = document.getElementById(`crew-char-${slot}`);
    if (!charSel) return;
    const value = id == null || id === '' ? '' : String(id);
    if (value && !Array.from(charSel.options).some((opt) => opt.value === value)) {
        charSel.add(new Option(name || value, value));
    }
    const previous = charSel.value;
    charSel.value = value;
    if (value && charSel.value !== value) {
        charSel.add(new Option(name || value, value));
        charSel.value = value;
    }
    syncSearchDropdownLabel(charSel.id);
    if (dispatchChange && previous !== charSel.value) {
        charSel.dispatchEvent(new Event('change', { bubbles: true }));
    }
};

const resetCastPreview = (previewEl) => {
    if (!previewEl) return;
    previewEl.style.backgroundImage = 'none';
    previewEl.classList.remove('has-bg-image');
    previewEl.querySelectorAll('img, video').forEach((n) => n.remove());
};

const applyCastSlotState = (slot, state) => {
    const charSel = document.getElementById(`crew-char-${slot}`);
    const roleSel = document.getElementById(`crew-role-${slot}`);
    const goalEl = document.getElementById(`message-${slot}`);
    const previewEl = document.getElementById(`character-${slot}-preview`);
    if (charSel) {
        setCrewCharacterSlot(slot, { id: state.charValue, name: state.charText }, { dispatchChange: false });
    }
    if (roleSel) {
        if (state.roleHtml) roleSel.innerHTML = state.roleHtml;
        roleSel.value = state.roleValue;
        const trigger = document.getElementById(`${roleSel.id}-search-trigger`);
        const label = trigger?.querySelector('.search-dropdown__label');
        if (label) label.textContent = state.roleText || state.roleValue || label.textContent;
        else syncSearchDropdownLabel(roleSel.id);
    }
    if (goalEl) goalEl.value = state.goal || '';
    if (previewEl) {
        previewEl.style.backgroundImage = 'none';
        previewEl.innerHTML = state.previewHtml || '';
    }
    const inputs = Array.from(getGreetingIntentGrid()?.querySelectorAll(`.greeting-intent-input[data-slot="${slot}"]`) || []);
    (state.greetingIntents || []).forEach((value, index) => {
        if (inputs[index]) inputs[index].value = value || '';
    });
    refreshScenarioCounters();
};

const isCastPreviewRequestCurrent = (slot, seq, charId, outfitId) => {
    if (seq !== previewSeqBySlot[slot]) return false;
    const charSel = document.getElementById(`crew-char-${slot}`);
    const roleSel = document.getElementById(`crew-role-${slot}`);
    if (!charSel || charSel.value !== charId) return false;
    if (!roleSel) return false;
    const currentOutfit = roleSel.value || '';
    if (outfitId && currentOutfit && currentOutfit !== outfitId) return false;
    return true;
};

const updatePreviewForSlot = async (slot) => {
    const seq = previewSeqBySlot[slot] + 1;
    previewSeqBySlot[slot] = seq;
    try {
        const charSel = document.getElementById(`crew-char-${slot}`);
        const roleSel = document.getElementById(`crew-role-${slot}`);
        const prevDiv = document.getElementById(`character-${slot}-preview`);
        if (!charSel || !roleSel || !prevDiv) return;

        const charId = charSel.value;
        if (!charId) {
            resetCastPreview(prevDiv);
            return;
        }

        let outfitId = roleSel.value;
        if (!outfitId) {
            const firstOpt = Array.from(roleSel.options).find(o => o.value && !isNaN(parseInt(o.value, 10)));
            outfitId = firstOpt ? firstOpt.value : '0';
        }
        if (typeof outfitId === 'string' && outfitId.startsWith('new-outfit-')) return;

        const gender = await getCharacterGender(charId);
        if (!isCastPreviewRequestCurrent(slot, seq, charId, outfitId)) return;
        const tupleKey = `${charId}:${outfitId}`;
        let actionId = castPreviewActionByTuple.get(tupleKey);
        if (actionId == null) {
            actionId = pickStandingActionId(gender);
            castPreviewActionByTuple.set(tupleKey, actionId);
        }
        const spriteData = await fetchCache.fetchCharacterSprite(
            String(parseInt(charId, 10)),
            actionId,
            String(parseInt(outfitId, 10) || 0),
            'female',
            false,
            false
        );
        const spriteUrl = spriteData?.spriteUrl;
        if (!spriteUrl || !isCastPreviewRequestCurrent(slot, seq, charId, outfitId)) return;
        const existingImg = prevDiv.querySelector('.cast-preview-img');
        const existingSrc = existingImg?.getAttribute('src') || '';
        if (existingImg && (existingSrc === spriteUrl || existingImg.src === spriteUrl || existingImg.currentSrc === spriteUrl)) {
            return;
        }
        prevDiv.innerHTML = `<img class="cast-preview-img" src="${spriteUrl}" alt="Preview">`;
        prevDiv.style.backgroundImage = 'none';
    } catch (e) {
        // Non-blocking
    }
};

const refreshVisibleCastPreviews = () => {
    const num = getVisibleCharacterCount();
    for (let slot = 1; slot <= num; slot += 1) {
        updatePreviewForSlot(slot);
    }
};

const reorderCastSlotsByCharacterId = () => {
    if (isReorderingCastSlots) return false;
    const num = getVisibleCharacterCount();
    const occupied = [];
    for (let slot = 1; slot <= num; slot += 1) {
        const state = getCastSlotState(slot);
        if (!state.charValue) continue;
        const characterId = parseInt(state.charValue, 10);
        occupied.push({ slot, characterId: Number.isFinite(characterId) ? characterId : Number.MAX_SAFE_INTEGER, state });
    }
    if (occupied.length < 2) return false;
    const sorted = [...occupied].sort((a, b) => compareNumericCharacterIds(a.characterId, b.characterId));
    const alreadyOrdered = sorted.every((item, index) => item.slot === occupied[index].slot);
    if (alreadyOrdered) return false;
    isReorderingCastSlots = true;
    try {
        const emptyStates = [];
        for (let slot = 1; slot <= num; slot += 1) {
            if (!occupied.some((item) => item.slot === slot)) emptyStates.push(getCastSlotState(slot));
        }
        sorted.forEach((item, index) => applyCastSlotState(index + 1, item.state));
        emptyStates.forEach((state, index) => applyCastSlotState(sorted.length + index + 1, state));
        refreshLastCrewBySlot();
        for (let slot = 1; slot <= num; slot += 1) {
            previewSeqBySlot[slot] += 1;
        }
    } finally {
        isReorderingCastSlots = false;
    }
    return true;
};

const updateCastPartyBackground = () => {
    const img = document.getElementById('scenario-cast-shared-bg');
    if (!img) return;
    const locationId = document.getElementById('generate-bg-1')?.value || '';
    const timeOfDay = document.getElementById('time-of-day-1')?.value || '0.3';
    let url = '';
    if (locationId && locationId.startsWith('new-bg-')) {
        const variantKey = `${locationId}.${timeOfDay}`;
        url = window.syntheticBackgroundPreviews?.[variantKey]
            || window.syntheticBackgroundPreviews?.[locationId]
            || '';
        if (!url) {
            const bgDiv = document.getElementById('bg-preview-1');
            const bgStyle = bgDiv?.style?.backgroundImage || '';
            const match = bgStyle.match(/url\(["']?(.*?)["']?\)/);
            url = match?.[1] || '';
        }
    } else if (locationId && /^\d+$/.test(locationId)) {
        url = getBackgroundService().constructBackgroundUrl(locationId, timeOfDay) || '';
    }
    if (url) {
        img.src = url;
        img.hidden = false;
        return;
    }
    img.removeAttribute('src');
    img.hidden = true;
};

/** @type {HTMLFormElement|null} */
const formElement = /** @type {any} */ (document.getElementById('scenario-form'));

let isClearingScenarioDraft = false;
let isResettingScenarioUI = false;

// Cache for characters used by picker modal
let cachedScenarioCharacters = [];

const scenarioStepState = {
    sections: [],
    currentStep: 0,
    maxStep: 0,
    listenerAttached: false,
    initialized: false
};

const SCENARIO_WIZARD_PROGRESS_KEY = 'scenario_wizard_progress_v2';

const scenarioWizardProgress = {
    currentStep: 0,
    completedSteps: new Set(),
    maxAllowedStep: 0
};

const readWizardProgress = () => {
    const parsed = storage.getJSON(SCENARIO_WIZARD_PROGRESS_KEY);
    if (!parsed) return;

    const completed = Array.isArray(parsed.completedSteps) ? parsed.completedSteps : [];
    scenarioWizardProgress.completedSteps = new Set(
        completed
            .map(v => parseInt(v, 10))
            .filter(v => Number.isFinite(v))
    );
    scenarioWizardProgress.currentStep = Number.isFinite(parsed.currentStep) ? parsed.currentStep : 0;
};

const persistWizardProgress = () => {
    try {
        storage.setJSON(SCENARIO_WIZARD_PROGRESS_KEY, {
            currentStep: scenarioWizardProgress.currentStep,
            completedSteps: Array.from(scenarioWizardProgress.completedSteps).sort((a, b) => a - b)
        });
    } catch (e) {
        console.warn('[Scenario] Failed to persist wizard progress', e);
    }
};

const recomputeMaxAllowedStep = () => {
    const completed = Array.from(scenarioWizardProgress.completedSteps);
    const maxCompleted = completed.length ? Math.max(...completed) : -1;
    scenarioWizardProgress.maxAllowedStep = Math.max(0, Math.min(scenarioStepState.maxStep, maxCompleted + 1));
};

const getScenarioStepTitle = (step) => {
    return ['Story Idea', 'Story Details', 'Setting', 'Cast', 'Banner'][step] || `Step ${step + 1}`;
};

const markScenarioStepComplete = (step) => {
    const normalized = Math.max(0, Math.min(step, scenarioStepState.maxStep));
    // Keep completion monotonic (if step N is complete, steps 0..N are complete).
    for (let i = 0; i <= normalized; i++) {
        scenarioWizardProgress.completedSteps.add(i);
    }
    persistWizardProgress();
};

const resetScenarioWizardProgress = () => {
    scenarioWizardProgress.currentStep = 0;
    scenarioWizardProgress.completedSteps = new Set();
    recomputeMaxAllowedStep();
    persistWizardProgress();
};

const inferWizardProgressFromForm = () => {
    const nextCompleted = new Set();

    const title = document.getElementById('adventure-title')?.value?.trim() || '';
    const desc = document.getElementById('scenario-description')?.value?.trim() || '';
    const bannerPrompt = document.getElementById('adventure-banner-prompt')?.value?.trim() || '';
    const bg = document.getElementById('generate-bg-1')?.value || '';
    const numChars = parseInt(document.getElementById('num-characters')?.value || '1', 10) || 1;

    // If story fields exist, consider the "AI idea" step done for restored sessions.
    if (title || desc || bannerPrompt) nextCompleted.add(0);

    if (title && desc) {
        nextCompleted.add(1);
    }

    const castValid = (() => {
        for (let i = 1; i <= numChars; i++) {
            const charId = document.getElementById(`crew-char-${i}`)?.value || '';
            const outfit = document.getElementById(`crew-role-${i}`)?.value || '';
            const statusEl = document.getElementById(`char-status-${i}`);
            const hasRedX = !!(statusEl && statusEl.classList.contains('fa-circle-xmark'));
            if (!charId || hasRedX || !outfit) return false;
        }
        return true;
    })();

    if (castValid) {
        nextCompleted.add(0);
        nextCompleted.add(1);
        nextCompleted.add(2);
    }

    if (bg) {
        nextCompleted.add(0);
        nextCompleted.add(1);
        nextCompleted.add(2);
        nextCompleted.add(3);
    }

    if (bannerPrompt || hasBannerPreviewMedia()) {
        nextCompleted.add(0);
        nextCompleted.add(1);
        nextCompleted.add(2);
        nextCompleted.add(3);
        nextCompleted.add(4);
    }

    scenarioWizardProgress.completedSteps = nextCompleted;
    recomputeMaxAllowedStep();
    persistWizardProgress();
};

const ensureSearchableSelect = (selectId, fallbackText = 'Select an option', overrides = null) => {
    const selectEl = document.getElementById(selectId);
    const placeholderOption = selectEl?.options?.[0];
    const placeholderText = placeholderOption?.textContent?.trim() || fallbackText;
    const config = {
        placeholder: placeholderText,
        searchLabel: placeholderText,
        searchPlaceholder: placeholderText,
        ...(overrides && typeof overrides === 'object' ? overrides : {})
    };
    const existing = window.searchDropdownRegistry?.[selectId];
    if (existing && existing.refresh) {
        existing.refresh(config);
        return;
    }
    initSearchDropdown(selectId, config);
};

const filterOutfitsByGender = (outfitsList = [], gender) => {
    const deduped = dedupeById(outfitsList);
    if (!gender || gender === 'all') return deduped;
    return deduped.filter(outfit => {
        const outfitId = parseInt(outfit.id, 10);
        if (outfitId === 0 || outfitId === 1) return true;
        const outfitGender = normalizeGenderForFilter(outfit.gender || outfit.gender_text || outfit.genderLabel);
        if (!outfitGender || outfitGender === 'all') return true;
        return outfitGender === gender;
    });
};

const prioritizeDefaultOutfit = (list, fallback) => moveDefaultToFront(list, fallback, {
    sortComparator: (a, b) => a.name.localeCompare(b.name),
    fallbackProps: { available: true },
});

const characterGenderCache = new Map();

const seedCharacterGenderCache = (characters = []) => {
    characters.forEach((character) => {
        const id = String(character?.id ?? '').trim();
        if (!id) return;
        const normalized = normalizeGenderForFilter(character?.gender);
        if (normalized) characterGenderCache.set(id, normalized);
    });
};

const getListedCharacterGender = (characterId) => {
    const listed = (cachedScenarioCharacters || []).find((character) => String(character?.id) === String(characterId));
    return normalizeGenderForFilter(listed?.gender);
};

const getCharacterGender = async (characterId) => {
    if (!characterId) return '';
    const cacheKey = String(characterId);
    if (characterGenderCache.has(cacheKey)) {
        return characterGenderCache.get(cacheKey);
    }
    const listedGender = getListedCharacterGender(cacheKey);
    if (listedGender) {
        characterGenderCache.set(cacheKey, listedGender);
        return listedGender;
    }
    try {
        const hangoutService = HangoutService.getInstance();
        const details = await hangoutService.getCharacterDetails(characterId);
        const normalized = normalizeGenderForFilter(details?.gender);
        characterGenderCache.set(cacheKey, normalized);
        return normalized;
    } catch (error) {
        console.warn(`[Scenario] Failed to fetch gender for character ${characterId}:`, error);
        return '';
    }
};

const updateScenarioHeaderState = (step) => {
    const clearBtn = document.getElementById('clear-draft-btn');
    if (clearBtn) clearBtn.classList.toggle('is-hidden', step === 0);
};

const setScenarioStepUnsafe = (step) => {
    if (!scenarioStepState.sections.length) return;
    const targetStep = Math.max(0, Math.min(step, scenarioStepState.maxStep));
    scenarioStepState.currentStep = targetStep;
    scenarioWizardProgress.currentStep = targetStep;
    persistWizardProgress();
    scenarioStepState.sections.forEach((section, index) => {
        const isActive = index === targetStep;
        section.classList.toggle('recruit-panel--active', isActive);
        section.classList.toggle('is-hidden', !isActive);
    });
    updateScenarioHeaderState(targetStep);
};

const setScenarioStep = (step) => {
    if (!scenarioStepState.sections.length) return;
    const targetStep = Math.max(0, Math.min(step, scenarioStepState.maxStep));

    recomputeMaxAllowedStep();
    const isBack = targetStep <= scenarioStepState.currentStep;
    const isAllowed = targetStep <= scenarioWizardProgress.maxAllowedStep;
    if (!isBack && !isAllowed) {
        const title = getScenarioStepTitle(targetStep);
        showWarning(`Finish the current step before unlocking "${title}".`, { icon: 'lock', duration: 3500 });
        return;
    }

    setScenarioStepUnsafe(targetStep);
};

const handleScenarioStepKeydown = (event) => {
    if (!scenarioStepState.initialized) return;
    const activeElement = document.activeElement;
    if (activeElement) {
        const tagName = activeElement.tagName;
        const maybeHtmlEl = /** @type {HTMLElement} */ (activeElement);
        if (tagName === 'INPUT' || tagName === 'TEXTAREA' || maybeHtmlEl.isContentEditable) return;
    }

    let nextStep = scenarioStepState.currentStep;
    const isPrevKey = event.key === 'PageUp' || event.key === 'ArrowLeft';
    const isNextKey = event.key === 'PageDown' || event.key === 'ArrowRight';

    if (isPrevKey && scenarioStepState.currentStep > 0) {
        nextStep -= 1;
    } else if (isNextKey && scenarioStepState.currentStep < scenarioStepState.maxStep) {
        nextStep += 1;
    } else {
        return;
    }

    event.preventDefault();
    // Ungated keyboard navigation: allow free movement regardless of strict wizard progress.
    setScenarioStepUnsafe(nextStep);
};

const initScenarioStepNavigation = () => {
    const form = document.getElementById('scenario-form');
    if (!form) return;

    const sections = Array.from(form.querySelectorAll('[data-step]'));
    if (!sections.length) return;

    scenarioStepState.sections = sections;
    scenarioStepState.maxStep = sections.length - 1;
    scenarioStepState.initialized = true;

    readWizardProgress();
    recomputeMaxAllowedStep();

    sections.forEach((section) => {
        section.classList.add('recruit-panel');
        if (!section.classList.contains('recruit-panel--active')) {
            section.classList.add('is-hidden');
        }
    });

    const presetIndex = sections.findIndex(section => section.classList.contains('recruit-panel--active'));
    const initial = presetIndex >= 0 ? presetIndex : 0;
    const requested = Number.isFinite(scenarioWizardProgress.currentStep) ? scenarioWizardProgress.currentStep : initial;
    setScenarioStep(Math.min(requested, scenarioWizardProgress.maxAllowedStep));

    // Always attach keyboard nav for PageUp/PageDown (forward movement still respects strict gating via setScenarioStep).
    if (!scenarioStepState.listenerAttached) {
        document.addEventListener('keydown', handleScenarioStepKeydown);
        scenarioStepState.listenerAttached = true;
    }

    window.scenarioWizard = {
        get currentStep() {
            return scenarioStepState.currentStep;
        },
        get totalSteps() {
            return scenarioStepState.maxStep + 1;
        },
        setStep: setScenarioStep,
        completeStep: markScenarioStepComplete,
        reset: resetScenarioWizardProgress,
        nextStep: () => setScenarioStep(Math.min(scenarioStepState.currentStep + 1, scenarioStepState.maxStep)),
        previousStep: () => setScenarioStep(Math.max(scenarioStepState.currentStep - 1, 0))
    };
};

const initStepValidationButtons = () => {
    const buttons = document.querySelectorAll('.step-validate-btn');
    if (!buttons.length) return;

    buttons.forEach((button) => {
        if (button.dataset.validationBound === 'true') return;
        button.dataset.validationBound = 'true';

        button.addEventListener('click', () => {
            const stepIndex = parseInt(button.dataset.stepIndex || '-1', 10);
            if (Number.isNaN(stepIndex)) return;

            const lengthError = getScenarioStepLengthError(stepIndex);
            if (lengthError) {
                showToast(lengthError, 'exclamation-triangle');
                return;
            }

            markScenarioStepComplete(stepIndex);

            if (stepIndex < scenarioStepState.maxStep) {
                setScenarioStep(stepIndex + 1);
            } else {
                showSuccess('All steps validated. Ready to submit.', { icon: 'check', duration: 3000 });
            }
        });
    });
};

const initStepBackButtons = () => {
    document.querySelectorAll('.step-back-btn').forEach((btn) => {
        if (btn.dataset.backBound === 'true') return;
        btn.dataset.backBound = 'true';
        btn.addEventListener('click', () => {
            setScenarioStep(scenarioStepState.currentStep - 1);
        });
    });
};

const setBg = (el, url) => {
    if (!el || !url) return;
    el.querySelectorAll('img, video').forEach((n) => n.remove());
    el.querySelector('.preview-placeholder')?.classList.add('is-hidden');

    if (url.startsWith('data:')) {
        const img = document.createElement('img');
        img.src = url;
        img.alt = 'Preview';
        img.className = el.id === 'banner-image-preview' ? 'preview-img preview-img--banner' : 'preview-img';
        el.appendChild(img);
        el.classList.remove('has-bg-image');
        el.style.backgroundImage = '';
        return;
    }

    el.classList.add('has-bg-image');
    el.style.backgroundImage = `url(${url})`;
};

const resetPreviewSurface = (el) => {
    if (!el) return;
    el.querySelectorAll('img, video').forEach((n) => n.remove());
    el.classList.remove('has-bg-image');
    el.style.backgroundImage = '';
    el.querySelector('.preview-placeholder')?.classList.remove('is-hidden');
};

const saveDraft = async () => {
    if (isClearingScenarioDraft) return;
    if (!formElement) return;
    const data = new FormData(formElement);
    const plainObj = {};
    for (const [k, v] of data.entries()) {
        plainObj[k] = v;
    }

    // Get existing draft early so we can preserve previews when the UI is in a transient loading state.
    const existingDraft = storage.getJSON(DRAFT_KEY) || {};

    // Helper to get preview URL (prefers embedded <img> then CSS bg)
    const getUrlFromDiv = (div) => {
        if (!div) return '';
        const img = div.querySelector('img');
        if (img?.src) return img.src;
        const bg = div.style.backgroundImage || '';
        const m = bg.match(/url\(["']?(.*?)["']?\)/);
        return m ? m[1] : '';
    };

    const bannerUrl = getUrlFromDiv(document.getElementById('banner-image-preview'));
    const backgroundUrl = getUrlFromDiv(document.getElementById('bg-preview-1'));

    // For characters: Save as slot-based object to maintain alignment
    const characterUrls = {};
    for (let i = 1; i <= 3; i++) {
        const previewDiv = document.getElementById(`character-${i}-preview`);
        const selectedVal = (document.getElementById(`crew-char-${i}`)?.value || '').toString();

        const url = getUrlFromDiv(previewDiv);
        if (url) {
            // If it's already a base64 data URL, use it directly
            if (url.startsWith('data:')) {
                characterUrls[i] = url;
            } else {
                // For CDN URLs, let them fail if they're going to fail
                characterUrls[i] = await getBase64FromUrl(url);
            }
        }
    }

    // Build background variants map if synthetic previews exist
    let backgroundVariants = null;
    let backgroundPrompts = null; // NEW: store base prompts per variant
    let backgroundName = null; // NEW: store background name
    if (window.syntheticBackgroundPreviews) {
        const bgSelectVal = document.getElementById('generate-bg-1')?.value || '';
        if (bgSelectVal && bgSelectVal.startsWith('new-bg-')) {
            backgroundVariants = {};
            backgroundPrompts = {};

            // Save the background name from the name input
            const nameInput = document.getElementById('custom-bg-name');
            if (nameInput && nameInput.value.trim()) {
                backgroundName = nameInput.value.trim();
            }

            Object.entries(window.syntheticBackgroundPreviews).forEach(([key, url]) => {
                if (key.startsWith(bgSelectVal)) {
                    // Extract time-of-day from cache key: new-bg-something.0.3 -> 0.3
                    let tod;
                    if (key === bgSelectVal) {
                        // Base key without time-of-day suffix
                        tod = key;
                    } else {
                        // Extract the time-of-day part after the background ID
                        const timeOfDayPart = key.substring(bgSelectVal.length + 1); // +1 for the dot
                        tod = timeOfDayPart || key;
                    }
                    backgroundVariants[tod] = url;
                    // pull base prompt from syntheticBackgroundPrompts map if available
                    if (window.syntheticBackgroundPrompts && window.syntheticBackgroundPrompts[key]) {
                        backgroundPrompts[key] = window.syntheticBackgroundPrompts[key]; // Use full key, not just tod
                    }
                }
            });
        }
    }

    // If banner is currently showing a loading skeleton (or otherwise empty), preserve the last known banner preview.
    const bannerToStore = bannerUrl ? await getBase64FromUrl(bannerUrl) : (existingDraft.__previews?.banner || '');

    plainObj.__bannerUploadSource = bannerUploadSource;

    plainObj.__previews = {
        banner: bannerToStore,
        background: backgroundVariants ? null : await getBase64FromUrl(backgroundUrl),
        characters: characterUrls
    };

    // Save background data outside of __previews to survive quota exceeded scenarios
    if (backgroundVariants) {
        plainObj.__backgroundVariants = backgroundVariants;
    }
    if (backgroundPrompts) {
        plainObj.__backgroundPrompts = backgroundPrompts;
    }
    if (backgroundName) {
        plainObj.__backgroundName = backgroundName;
    }

    // Offload heavy base64 previews into IndexedDB and keep lightweight placeholders
    const embedOrStore = async (str, oldPreviewRef = null) => {
        if (!str) return '';
        if (str.startsWith('data:') && str.length > 100_000) {
            try {
                const id = await cleanupAndSavePreview(str, oldPreviewRef);
                return `db:${id}`;
            } catch (e) {
                console.warn('[Scenario] cleanupAndSavePreview failed', e);
                return str; // fallback keep base64 (may trigger quota)
            }
        } else {
            // Clean up old preview if it was stored in IndexedDB
            if (oldPreviewRef && oldPreviewRef.startsWith('db:')) {
                const oldId = oldPreviewRef.slice(3);
                await deletePreview(oldId);
            }
        }
        return str;
    };

    plainObj.__previews.banner = await embedOrStore(plainObj.__previews.banner, existingDraft.__previews?.banner);
    if (!backgroundVariants) {
        plainObj.__previews.background = await embedOrStore(plainObj.__previews.background, existingDraft.__previews?.background);
    } else {
        for (const todKey of Object.keys(backgroundVariants)) {
            const oldRef = existingDraft.__backgroundVariants?.[todKey];
            backgroundVariants[todKey] = await embedOrStore(backgroundVariants[todKey], oldRef);
        }
    }

    // Process character previews object
    for (const slot of Object.keys(plainObj.__previews.characters)) {
        const oldRef = existingDraft.__previews?.characters?.[slot];
        plainObj.__previews.characters[slot] = await embedOrStore(plainObj.__previews.characters[slot], oldRef);
    }

    // Also save dropdown states including synthetics
    // Keep previous dropdown data to avoid overwriting with blanks caused by
    // Select-2 re-initialisation or other transient resets.
    const prevDraft = storage.getJSON(DRAFT_KEY);
    const prevChars = prevDraft?.__dropdowns?.characters || {};

    // Save dropdown states as slot-based objects to maintain alignment
    const charactersData = {};
    for (let i = 1; i <= 3; i++) {
        const sel = document.getElementById(`crew-char-${i}`);
        const role = document.getElementById(`crew-role-${i}`);

        const val = sel?.value || '';
        const roleVal = role?.value || '';
        const roleText = role?.options[role.selectedIndex]?.textContent || roleVal;
        const txt = sel?.options[sel.selectedIndex]?.textContent || val;

        // If the dropdown currently has no meaningful value, keep whatever
        // we stored previously so we don't wipe out good data.
        if (!val) {
            charactersData[i] = prevChars[i] || { value: '', text: '', outfitRole: '', goal: '', greeting_intents: [] };
            continue;
        }

        const previewDiv = document.getElementById(`character-${i}-preview`);
        const goal = document.getElementById(`message-${i}`)?.value?.trim() || '';
        const greetingIntents = Array.from(getGreetingIntentGrid()?.querySelectorAll(`.greeting-intent-input[data-slot="${i}"]`) || []).map((input) => input.value);

        // Keep prior display name if current option text is missing
        const previewUrl = getUrlFromDiv(previewDiv) || '';
        const previousText = prevChars[i]?.text || '';

        const result = {
            value: val,
            text: txt || previousText || val,
            outfitRole: roleVal || '',
            outfitRoleTxt: roleText || roleVal,
            goal: goal || '',
            greeting_intents: greetingIntents,
            gender: prevChars[i]?.gender || 'unknown',
            previewUrl
        };

        // Store the result for this slot
        charactersData[i] = result;
    }

    // Save AI assistant fields that aren't captured by FormData
    plainObj.__aiFields = {
        'ai-request': document.getElementById('ai-request')?.value || ''
    };

    plainObj.__dropdowns = {
        characters: charactersData,
        identities: { ...identityByCharId },
        background: {
            value: document.getElementById('generate-bg-1')?.value || '',
            text: (document.getElementById('generate-bg-1')?.options[document.getElementById('generate-bg-1')?.selectedIndex]?.textContent || '').replace(/\s*\(New\)$/, '').trim(),
            timeOfDay: document.getElementById('time-of-day-1')?.value || '0.1'
        }
    };


    try {
        storage.setJSON(DRAFT_KEY, plainObj);
    } catch (err) {
        console.warn('[Scenario] Failed to save full draft – quota exceeded, retrying without previews', err);
        const minimal = { ...plainObj };
        delete minimal.__previews;                // previews are the heavy part
        try {
            storage.setJSON(DRAFT_KEY, minimal);
        } catch (e2) {
            console.error('[Scenario] Still could not save draft', e2);
        }
    }
    // Expose for external manual saves (e.g., AI assistant redirection)
    if (typeof window !== 'undefined') window.saveScenarioDraft = saveDraft;
};

// Function to refresh character dropdowns (e.g., after returning from character creation)
const refreshCharacterDropdowns = async () => {
    try {
        // Load characters from CDN (same as createVideoPage.js)
        // characters_rankings.json has no gender field; gender comes from characters_rankings_modal.json
        const [data, modalData] = await Promise.all([
            fetchCache.fetchCharacterRankings(),
            fetchCache.fetchJSON(catalogJsonUrl('data/characters/characters_rankings_modal.json')),
        ]);

        let privateList = [];
        if (getCurrentUserId()) {
            try {
                privateList = await fetchPrivateCharacters();
            } catch (e) {
                console.warn('[Scenario] Failed to fetch private characters:', e);
            }
        }

        const characters = mapCreateCharacters({ rankings: data, modalData, privateList });
        if (!characters.length) return;

        cachedScenarioCharacters = characters;
        seedCharacterGenderCache(characters);

        // Preserve current selections
        const current = [1, 2, 3].map(i => {
            const sel = document.getElementById(`crew-char-${i}`);
            if (!sel) return null;
            const val = sel.value;
            const text = sel.options[sel.selectedIndex]?.textContent;
            return { val, text };
        });

        // Clear and add all CDN characters, but preserve existing selected text if present
        [1, 2, 3].forEach(i => {
            const dropdown = document.getElementById(`crew-char-${i}`);
            if (!dropdown) return;

            const currentValue = dropdown.value || '';
            const currentText = dropdown.options[dropdown.selectedIndex]?.textContent || '';

            // Clear all existing options
            dropdown.innerHTML = '';

            // Add placeholder option first
            const placeholderOption = new Option('Select a character', '');
            placeholderOption.setAttribute('data-translate', 'common.select_character');
            dropdown.add(placeholderOption);

            characters.forEach(char => {
                const opt = new Option(char.name, char.id);
                dropdown.add(opt);
            });

            // Restore previous selection and its label when possible
            if (currentValue) {
                // Ensure option exists for the current value
                if (!Array.from(dropdown.options).some(o => o.value === currentValue)) {
                    dropdown.add(new Option(currentText || currentValue, currentValue));
                }
                dropdown.value = currentValue;
            }
        });

        // Restore selections
        current.forEach((saved, idx) => {
            if (!saved) return;
            const dropdown = document.getElementById(`crew-char-${idx + 1}`);
            if (!dropdown) return;
            // Ensure the visible label matches the prior saved text
            if (saved.val && !Array.from(dropdown.options).some(o => o.value === saved.val)) {
                dropdown.add(new Option(saved.text || saved.val, saved.val));
            } else {
                const opt = Array.from(dropdown.options).find(o => o.value === saved.val);
                if (opt && saved.text) opt.textContent = saved.text;
            }
            dropdown.value = saved.val;
        });

        [1, 2, 3].forEach(i => ensureSearchableSelect(`crew-char-${i}`, 'Select a character'));
        
        // Attach picker modal to character dropdowns
        attachScenarioCharacterPickers();

    } catch (error) {
        console.error('CDN character refresh error:', error);
    }
};

// Attach ranked character picker modal to crew-char dropdowns
const attachScenarioCharacterPickers = () => {
    [1, 2, 3].forEach(i => {
        const selectId = `crew-char-${i}`;
        const trigger = document.getElementById(`${selectId}-search-trigger`);
        if (!trigger || trigger.dataset.scenarioPickerAttached) return;
        trigger.dataset.scenarioPickerAttached = 'true';
        
        attachCreateVideoRankedCharacterPickerToTrigger({
            selectId,
            getCharacters: () => getScenarioCharactersForPicker(),
            getFilterIds: null,
            getTakenIds: () => {
                const num = getVisibleCharacterCount();
                const ids = [];
                for (let slot = 1; slot <= num; slot += 1) {
                    if (slot === i) continue;
                    const charId = document.getElementById(`crew-char-${slot}`)?.value;
                    if (charId) ids.push(charId);
                }
                return ids;
            }
        });
    });
};

const restoreDraft = async () => {
    if (!formElement) {
        return;
    }
    const draft = storage.getJSON(DRAFT_KEY);
    if (!draft) return;

    isRestoringScenarioDraft = true;
    try {

    // If this user has an existing draft but no wizard progress stored, derive progress once.
    // This keeps strict step gating while still allowing users to resume where they left off.
    const hasStoredWizardProgress = !!storage.getJSON(SCENARIO_WIZARD_PROGRESS_KEY);

    // Sync the number-of-characters select to how many character slots were actually saved
    const desiredCount = draft.__dropdowns?.characters ?
        Object.values(draft.__dropdowns.characters).filter(c => c && c.value).length : 1;
    const numSel = document.getElementById('num-characters');
    if (numSel && numSel.value !== String(desiredCount)) {
        numSel.value = String(desiredCount);
        numSel.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Restore character slot dropdowns from draft
    const intentsBySlot = {};
    if (draft.__dropdowns && draft.__dropdowns.characters) {
        for (let slot = 1; slot <= 3; slot++) {
            const saved = draft.__dropdowns.characters[slot];
            if (!saved) continue;

            const sel = document.getElementById(`crew-char-${slot}`);
            const role = document.getElementById(`crew-role-${slot}`);
            if (!sel) continue;

            // Ensure the saved character option exists and select it.
            if (saved.value) {
                // Real character IDs should still be forced into the select in case CDN list isn't loaded yet.
                addOptionIfNotExist(sel, saved.value, saved.text || `Character ${slot}`);
                sel.value = saved.value;
                sel.dispatchEvent(new Event('change', { bubbles: true }));
            }

            // restore outfit dropdown value
            if (role && saved.outfitRole) {
                addOptionIfNotExist(role, saved.outfitRole, saved.outfitRoleTxt || `Outfit ${saved.outfitRole}`);
                role.value = saved.outfitRole;
            }

            // Restore character goal message
            const messageField = document.getElementById(`message-${slot}`);
            if (messageField && saved.goal) {
                messageField.value = saved.goal;
            }
            const greetingIntents = Array.isArray(saved.greeting_intents)
                ? saved.greeting_intents
                : (saved.greeting_intent ? [saved.greeting_intent] : []);
            intentsBySlot[slot] = greetingIntents;
        }
        setGreetingIntentsFromDraft(intentsBySlot);
    }

    // Now refresh dropdowns (will preserve synthetics via updated logic)
    await refreshCharacterDropdowns();

    // Update outfit dropdowns for restored characters
    for (let slot = 1; slot <= 3; slot++) {
        const charSel = document.getElementById(`crew-char-${slot}`);
        if (charSel && charSel.value) {
            await updateRoleDropdown(slot, { forceDefault: false });
        }
    }

    // Set form values (now options exist)
    Object.entries(draft).forEach(([key, val]) => {
        if (key.startsWith('__')) return;
        const field = formElement.elements[key];
        if (field) {
            field.value = val;
            field.dispatchEvent(new Event('change', { bubbles: true }));
        }
    });

    // Restore visibility button state (stored via hidden input)
    {
        const visibilityHidden = document.getElementById('scenario-visibility');
        const publicBtn = document.getElementById('public-btn');
        const privateBtn = document.getElementById('private-btn');
        const value = (visibilityHidden?.value || '').toLowerCase();
        if (publicBtn && privateBtn) {
            const isPriv = value === 'private';
            publicBtn.classList.toggle('active', !isPriv);
            privateBtn.classList.toggle('active', isPriv);
            publicBtn.setAttribute('aria-pressed', String(!isPriv));
            privateBtn.setAttribute('aria-pressed', String(isPriv));
        }
        if (visibilityHidden && value !== 'public' && value !== 'private') {
            visibilityHidden.value = 'public';
        }
    }

    // Restore AI assistant fields that aren't part of the main form
    if (draft.__aiFields) {
        Object.entries(draft.__aiFields).forEach(([fieldId, value]) => {
            const field = document.getElementById(fieldId);
            if (field && value) {
                field.value = value;
                field.dispatchEvent(new Event('change', { bubbles: true }));
            }
        });
    }

    // Ensure character & background options exist even if not in IndexedDB yet
    if (draft.__dropdowns && draft.__dropdowns.characters) {
        const charactersData = draft.__dropdowns.characters;
        if (charactersData && typeof charactersData === 'object' && !Array.isArray(charactersData)) {
            for (const slot of Object.keys(charactersData)) {
                const saved = charactersData[slot];
                if (!saved) continue;
                const sel = document.getElementById(`crew-char-${slot}`);
                if (sel && !Array.from(sel.options).some(o => o.value === saved.value)) {
                    addOptionIfNotExist(sel, saved.value, saved.text || `Character ${slot}`);
                }
                if (sel && saved.value && (!sel.value || sel.value !== saved.value)) {
                    sel.value = saved.value;
                    sel.dispatchEvent(new Event('change', { bubbles: true }));
                }
            }
        }
        if (draft.__dropdowns.background) {
            const bgSel = document.getElementById('generate-bg-1');
            const bg = draft.__dropdowns.background;
            if (bgSel && bg.value) {
                let opt = Array.from(bgSel.options).find(o => o.value === bg.value);
                if (!opt) {
                    const displayText = bg.value.startsWith('new-bg-') ? `${bg.text} (New)` : bg.text;
                    addOptionIfNotExist(bgSel, bg.value, displayText || 'Background');
                } else if (bg.text && opt.textContent !== bg.text) {
                    const displayText = bg.value.startsWith('new-bg-') ? `${bg.text} (New)` : bg.text;
                    opt.textContent = displayText;
                }
                bgSel.value = bg.value;
            }
            if (bg.timeOfDay) {
                const timeOfDaySelect = document.getElementById('time-of-day-1');
                if (timeOfDaySelect) {
                    timeOfDaySelect.value = bg.timeOfDay;
                }
            }
        }
    }

    if (draft.__previews) {

        const resolvePreview = async (str) => {
            if (!str) return '';
            if (str.startsWith('db:')) {
                const id = str.slice(3);
                const data = await loadPreview(id);
                return data || '';
            }
            return str;
        };

        const bannerURL = await resolvePreview(draft.__previews.banner);
        let backgroundURL;
        const savedBackgroundVariants = draft.__backgroundVariants;
        if (savedBackgroundVariants) {
            const todSel = document.getElementById('time-of-day-1');
            const todKey = todSel?.value || '0.1';

            // Draft stores variants with just time-of-day keys like "0.1", "0.2", "0.3"
            const variantUrl = savedBackgroundVariants[todKey];
            if (variantUrl) {
                backgroundURL = await resolvePreview(variantUrl);
            } else {
                // fallback to first available
                const first = Object.values(savedBackgroundVariants)[0];
                backgroundURL = await resolvePreview(first);
            }

            // Seed cache with proper synthetic keys for current background
            window.syntheticBackgroundPreviews = window.syntheticBackgroundPreviews || {};
            const bg = draft.__dropdowns?.background;
            if (bg?.value?.startsWith('new-bg-')) {
                Object.entries(savedBackgroundVariants).forEach(([todKey, url]) => {
                    const fullKey = `${bg.value}.${todKey}`;
                    window.syntheticBackgroundPreviews[fullKey] = url;
                });
            } else {
                // For non-synthetic backgrounds, use original keys
                Object.entries(savedBackgroundVariants).forEach(([k, v]) => {
                    window.syntheticBackgroundPreviews[k] = v;
                });
            }
        } else {
            backgroundURL = await resolvePreview(draft.__previews.background);
        }

        // Handle characters as object {slot: url} format
        const characterURLs = {};
        if (draft.__previews.characters) {
            for (const slot of Object.keys(draft.__previews.characters)) {
                characterURLs[slot] = await resolvePreview(draft.__previews.characters[slot]);
            }
        }



        setBg(document.getElementById('banner-image-preview'), bannerURL);
        bannerUploadSource = draft.__bannerUploadSource === 'upload' ? 'upload' : 'prompt';
        setBg(document.getElementById('bg-preview-1'), backgroundURL);
        for (let slot = 1; slot <= 3; slot++) {
            const url = characterURLs[slot];
            const charDiv = document.getElementById(`character-${slot}-preview`);
            if (!charDiv || !url) continue;
            setBg(charDiv, url);
        }

        window.syntheticBackgroundPreviews = window.syntheticBackgroundPreviews || {};
        const restoredBackgroundVariants = draft.__backgroundVariants;
        if (restoredBackgroundVariants) {
            Object.assign(window.syntheticBackgroundPreviews, restoredBackgroundVariants);
        }

        const bg = draft.__dropdowns?.background;
        if (bg?.value?.startsWith('new-bg-') && backgroundURL) {
            window.syntheticBackgroundPreviews[bg.value] = backgroundURL;
        }
        if (draft.__backgroundPrompts) {
            window.syntheticBackgroundPrompts = window.syntheticBackgroundPrompts || {};
            Object.assign(window.syntheticBackgroundPrompts, draft.__backgroundPrompts);
        }

        if (draft.__backgroundName) {
            if (typeof window.showCustomBackgroundUI === 'function') {
                window.showCustomBackgroundUI();
            }
            const nameInput = document.getElementById('custom-bg-name');
            if (nameInput) {
                nameInput.value = draft.__backgroundName;
                const bgSelect = document.getElementById('generate-bg-1');
                const currentBgValue = bgSelect?.value;
                if (bgSelect && currentBgValue && currentBgValue.startsWith('new-bg-')) {
                    const selectedOption = bgSelect.querySelector(`option[value="${currentBgValue}"]`);
                    if (selectedOption) {
                        selectedOption.textContent = `${draft.__backgroundName} (New)`;
                    }
                }
                const txtArea = document.getElementById('custom-bg-textarea');
                if (txtArea && draft.__backgroundPrompts) {
                    const bgSelVal = document.getElementById('generate-bg-1')?.value || '';
                    const savedTimeOfDay = bg?.timeOfDay || '0.1';
                    const promptToRestore = draft.__backgroundPrompts[`${bgSelVal}.${savedTimeOfDay}`];
                    if (promptToRestore && promptToRestore !== 'detailed background') {
                        txtArea.value = promptToRestore;
                    }
                }
            }
        }
    }

    if (!hasStoredWizardProgress) {
        inferWizardProgressFromForm();
        setScenarioStep(Math.min(scenarioStepState.currentStep, scenarioWizardProgress.maxAllowedStep));
    }
    } finally {
        isRestoringScenarioDraft = false;
        Object.keys(identityByCharId).forEach((key) => delete identityByCharId[key]);
        const savedIds = draft.__dropdowns?.identities;
        if (savedIds && typeof savedIds === 'object') {
            Object.entries(savedIds).forEach(([id, letter]) => {
                if (id && CHAR_IDENTITY_LETTERS.includes(letter)) identityByCharId[String(id)] = letter;
            });
        }
        const usedLetters = new Set(Object.values(identityByCharId));
        for (let slot = 1; slot <= getVisibleCharacterCount(); slot += 1) {
            const charId = document.getElementById(`crew-char-${slot}`)?.value || '';
            if (!charId || identityByCharId[charId]) continue;
            const letter = CHAR_IDENTITY_LETTERS.find((item) => !usedLetters.has(item));
            if (!letter) continue;
            identityByCharId[charId] = letter;
            usedLetters.add(letter);
        }
        clearHiddenCrewSlots();
        refreshLastCrewBySlot();
        refreshScenarioCounters();
    }
};

// Helper to update character status icons with two states
const setCharStatusIcon = (idx, status) => {
    const el = document.getElementById(`char-status-${idx}`);
    if (!el) return;

    if (status === 'exists') {
        el.className = 'status-icon fa-solid fa-circle-check';
        el.style.color = 'var(--color-success)';
        el.title = 'Character created';
        el.dataset.state = 'exists';
    } else {
        el.className = 'status-icon';
        el.style.color = 'transparent';
        el.title = '';
        el.dataset.state = 'none';
    }
    el.style.display = 'inline';
};


// Feed the ranked character picker with CDN rankings.
const getScenarioCharactersForPicker = () => {
    const merged = [];
    const seen = new Set();

    const pushUnique = (item) => {
        const id = String(item?.id ?? '').trim();
        if (!id || seen.has(id)) return;
        seen.add(id);
        merged.push(item);
    };

    // Append CDN-ranked characters (deduped by ID).
    (Array.isArray(cachedScenarioCharacters) ? cachedScenarioCharacters : []).forEach((c) => {
        const id = c?.id ?? c?.character_id ?? '';
        pushUnique({ ...c, id });
    });

    return merged;
};

const refreshCharacterSlotStatuses = () => {
    if (isClearingScenarioDraft || isResettingScenarioUI) return;
    for (let i = 1; i <= 3; i++) {
        const select = document.getElementById(`crew-char-${i}`);
        if (!select) continue;
        const val = select.value;
        if (val && !isNaN(parseInt(val))) {
            setCharStatusIcon(i, 'exists');
        } else if (!val || val === '') {
            setCharStatusIcon(i, false);
        }
    }
};

const refreshSyntheticBackground = async () => {
    // Backgrounds are created with scenarios, not separately like characters
    // So we just need to ensure synthetic background options show proper names
    const select = document.getElementById('generate-bg-1');
    if (!select) return;
    const val = select.value;
    if (!val || !val.startsWith('new-bg-')) return;

    // Just ensure the option text is clean (no extra "(New)" suffixes if added multiple times)
    const currentOpt = select.options[select.selectedIndex];
    if (currentOpt && currentOpt.textContent.includes('(New)')) {
        const cleanName = currentOpt.textContent.replace(/\s*\(New\)$/, '').trim();
        if (cleanName && !currentOpt.textContent.endsWith(cleanName)) {
            currentOpt.textContent = cleanName;
        }
    }
};

// Field length validation function for scenario creation
const validateScenarioFieldLengths = (formData) => {
    const validationRules = [
        { field: 'adventure-title', maxLength: 50, label: 'Title' },
        { field: 'scenario-description', maxLength: 750, label: 'Scenario Description' },
        { field: 'adventure-banner-prompt', maxLength: 250, label: 'Banner Prompt' }
    ];

    for (const rule of validationRules) {
        const value = formData[rule.field] || '';
        if (value.length > rule.maxLength) {
            return {
                isValid: false,
                field: rule.field,
                message: `${rule.label} field is too long (${value.length}/${rule.maxLength} characters). Please shorten it.`
            };
        }
    }

    return { isValid: true };
};

let isSubmitting = false;

const requireScenarioAuth = async () => {
    const oauthManager = initOAuthManager();
    if (await oauthManager.isAuthenticated()) return true;
    showLoginRequired();
    return false;
};

// Simple debounce helper to avoid excessive draft writes
const debounce = (fn, delay = 500) => {
    let t;
    return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(null, args), delay);
    };
};

// Resolve an outfit id by its display name, fetching CDN list if needed
const resolveOutfitIdByName = async (name) => {
    if (!name) return null;
    let outfits = window.outfitsData;
    try {
        if (!outfits || !Array.isArray(outfits)) {
            const { outfits: loaded } = await fetchOutfitsFromCdn(getCurrentLanguage());
            outfits = loaded;
            window.outfitsData = outfits;
        }
        if (!outfits || !Array.isArray(outfits)) return null;
        const normalize = (s) => String(s || '').toLowerCase().trim();
        const target = normalize(name).replace(/\s+outfit$/, '');
        const found = outfits.find(o => normalize(o.name) === target);
        return found ? parseInt(found.id, 10) : null;
    } catch {
        return null;
    }
};

/**
 * Gets all outfits for the character with availability information.
 * @param {string|number} characterId
 * @returns {Promise<Array>} Array of outfits with {id, name, available} properties
 */
const getCharacterOutfits = async (characterId) => {
    // Get all outfits from cache
    const { outfits } = await ensureOutfitsCache();
    const buildFilteredList = (gender) => {
        const enriched = (outfits || []).map(o => ({ ...o, available: true }));
        return prioritizeDefaultOutfit(filterOutfitsByGender(enriched, gender), outfits);
    };

    if (!characterId) {
        return buildFilteredList('');
    }

    try {
        const availableOutfitIds = await fetchCache.fetchJSON(`${WORKER_URL}/character/outfits?character_id=${characterId}`);
        const availableIdSet = new Set(availableOutfitIds.map(id => parseInt(id, 10)));

        const enrichedOutfits = (outfits || []).map(o => ({
            ...o,
            available: availableIdSet.has(parseInt(o.id, 10))
        }));
        const gender = await getCharacterGender(characterId);
        return prioritizeDefaultOutfit(filterOutfitsByGender(enrichedOutfits, gender), outfits);
    } catch (error) {
        console.warn(`[Scenario] Failed to fetch outfits from worker for ${characterId}, falling back to gender-based filtering:`, error);
        const hangoutService = HangoutService.getInstance();
        const details = await hangoutService.getCharacterDetails(characterId).catch(() => null);
        if (!details) return [];
        const normalizedGender = normalizeGenderForFilter(details.gender || 'female');
        characterGenderCache.set(String(characterId), normalizedGender);
        const enriched = (outfits || []).map(o => ({ ...o, available: true }));
        return prioritizeDefaultOutfit(filterOutfitsByGender(enriched, normalizedGender), outfits);
    }
};

const updateRoleDropdown = async (slot, options = {}) => {
    const { forceDefault = false } = options;
    const charSel = document.getElementById(`crew-char-${slot}`);
    const roleSel = document.getElementById(`crew-role-${slot}`);
    if (!charSel || !roleSel) return;

    const characterId = charSel.value;
    const savedRole = forceDefault ? '' : roleSel.value;

    const allOutfits = await getCharacterOutfits(characterId);
    roleSel.innerHTML = '';

    allOutfits.forEach(o => {
        const opt = document.createElement('option');
        opt.value = String(o.id);
        opt.textContent = o.name;

        // Style unavailable outfits as red and disabled
        if (!o.available) {
            opt.style.color = 'var(--color-error)';
            opt.disabled = true;
            opt.title = 'This outfit is not available for this character';
        }

        roleSel.appendChild(opt);
    });

    const pickDefaultValue = () => {
        const defaultOption = Array.from(roleSel.options).find(o => o.value === '0' && !o.disabled);
        if (defaultOption) return '0';
        const firstAvailable = Array.from(roleSel.options).find(o => !o.disabled);
        return firstAvailable ? firstAvailable.value : '';
    };

    let nextValue = '';
    if (savedRole) {
        const savedOption = Array.from(roleSel.options).find(o => o.value === savedRole);
        if (savedOption && !savedOption.disabled) {
            nextValue = savedRole;
        }
    }
    if (!nextValue) {
        nextValue = pickDefaultValue();
    }
    roleSel.value = nextValue || '';

    applyTranslations('create-scenario', getCurrentLanguage());
    reindexGreetingIntentRows();
    roleSel.dispatchEvent(new Event('change', { bubbles: true }));
    ensureSearchableSelect(`crew-role-${slot}`, 'Select an outfit');
};

const initCounters = () => {
    TEXT_FIELDS.forEach(({ id, counter, max }) => setupCharacterCount(id, counter, max));
    refreshCastCounters();
};

// ===== Outfits (CDN) =====
let outfitsCache = { lang: null, outfits: null };

const getCurrentLanguage = () => getTranslationLanguage() || 'en';

const fetchOutfitsFromCdn = async (lang) => {
    try {
        const data = await fetchCache.fetchOutfits(lang);
        const outfits = unwrapOutfitsPayload(data);
        return { outfits };
    } catch (e) {
        console.warn('Failed to fetch outfits from CDN', e);
        return { outfits: [] };
    }
};

const ensureOutfitsCache = async () => {
    const lang = getCurrentLanguage();
    if (outfitsCache.lang !== lang || !outfitsCache.outfits) {
        const { outfits } = await fetchOutfitsFromCdn(lang);
        outfitsCache = { lang, outfits };
        window.outfitsData = outfits;
    }
    return outfitsCache;
};

const populateRoles = async () => {
    for (let i = 1; i <= 3; i++) {
        await updateRoleDropdown(i, { forceDefault: true });
    }
};

const syncScenarioIdeaPlaceholder = () => {
    const el = document.getElementById('ai-request');
    if (!el) return;
    el.setAttribute(
        'data-translate-placeholder',
        getNsfwEnabled() ? 'steps.idea.placeholder' : 'steps.idea.placeholder_sfw'
    );
};

export async function initializeScenarioForm() {
    syncScenarioIdeaPlaceholder();
    if (!window.__scenarioIdeaPlaceholderBound__) {
        window.__scenarioIdeaPlaceholderBound__ = true;
        document.addEventListener('nsfwPreferenceSelected', () => {
            syncScenarioIdeaPlaceholder();
            applyTranslations('create-scenario', getTranslationLanguage());
        });
    }

    if (window.__scenarioInitDone__) {
        return;
    }
    window.__scenarioInitDone__ = true;

    initScenarioStepNavigation();
    initStepValidationButtons();
    initStepBackButtons();

    // Initialize search dropdowns early (before character loading)
    ensureSearchableSelect('num-characters', 'How many characters are in this story?', {
        hideSearch: true,
        ariaLabel: 'How many characters are in this story?'
    });
    [1, 2, 3].forEach(i => ensureSearchableSelect(`crew-char-${i}`, 'Select a character'));
    [1, 2, 3].forEach(i => ensureSearchableSelect(`crew-role-${i}`, 'Select an outfit'));
    ensureSearchableSelect('generate-bg-1', 'Pick a location');
    ensureSearchableSelect('time-of-day-1', 'Select time of day', {
        hideSearch: true,
        ariaLabel: 'Select time of day'
    });
    attachCreateLocationPickerToTrigger(/** @type {any} */ ({ selectId: 'generate-bg-1' }));

    const { initializeScenarioTags, syncScenarioTagsPickerFromForm } = await import('../tags/scenarioTags.js');
    await initializeScenarioTags();
    {
        const form = document.getElementById('scenario-form');
        if (form && !form.dataset.submitBound) {
            form.dataset.submitBound = 'true';
            form.addEventListener('submit', handleScenarioFormSubmission);
        }
    }
    {
        const visibilityButtons = Array.from(document.querySelectorAll('.visibility-btn'));
        if (visibilityButtons.length) {
            const visibilityHiddenInput = document.getElementById('scenario-visibility');
            visibilityButtons.forEach((btn) => {
                if (btn.dataset.visibilityBound) return;
                btn.dataset.visibilityBound = 'true';
                btn.addEventListener('click', () => {
                    visibilityButtons.forEach(b => b.classList.remove('active'));
                    btn.classList.add('active');

                    // Keep a draft-friendly value in sync (and set ARIA pressed state)
                    const nextVisibility = btn.dataset.visibility || (btn.id === 'private-btn' ? 'private' : 'public');
                    if (visibilityHiddenInput) visibilityHiddenInput.value = nextVisibility;
                    visibilityButtons.forEach(b => b.setAttribute('aria-pressed', String(b === btn)));
                });
            });
        }
    }
    initCounters();

    // After counters, attempt to refresh synthetic characters if any were created
    refreshCharacterSlotStatuses();

    // Restore previously saved draft if any
    await restoreDraft();
    syncScenarioTagsPickerFromForm();
    syncGreetingIntentSlotVisibility();
    reorderCastSlotsByCharacterId();
    updateCastPartyBackground();



    // Ensure character dropdowns are populated with CDN data (initial load or refresh)
    await refreshCharacterDropdowns();

    // After restoring draft, attempt refresh again now that selections are set
    refreshCharacterSlotStatuses();
    await refreshSyntheticBackground();

    // If a scenario is currently queued, show the queued step immediately (survives reloads).
    renderScenarioQueuedCardInline().catch(() => { });
    // Some init paths can flip the visible step back; re-assert on next tick so we "win last".
    setTimeout(() => { renderScenarioQueuedCardInline().catch(() => { }); }, 0);

    // Sprite completion notices only. Scenario Start does not wait on these:
    // the row id from create-scenario is enough, and hangout loads a missing R2 file from the database.
    let lastNotificationCheck = Date.now();

    const postScenarioNotification = (path, token, body) => fetchCache.fetchJSON(`${USER_WORKER_URL}${path}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
        },
        body,
        skipCache: true,
        allowUnsafeRetry: true
    });

    const checkForSpriteNotifications = async () => {
        try {
            const oauthManager = initOAuthManager();
            const authed = await oauthManager.isAuthenticated();
            if (!authed) {
                return;
            }

            const token = await oauthManager.getValidToken();
            const data = await postScenarioNotification('/notifications', token, { limit: 30, unread_only: false });
            const notifications = data.notifications || [];
            let newestSeen = lastNotificationCheck;

            for (const notification of notifications) {
                const createdAtMs = new Date(notification.created_at).getTime();
                if (!Number.isNaN(createdAtMs)) {
                    newestSeen = Math.max(newestSeen, createdAtMs);
                }

                if (notification.type === 'sprite_generation' &&
                    createdAtMs > lastNotificationCheck) {
                    const params = notification.params || [];
                    const characterId = params[1];

                    if (!characterId) {
                        console.warn(`[Scenario] Missing character_id in notification. This is expected for notifications created before the fix. Notification:`, {
                            id: notification.notification_id,
                            type: notification.type,
                            message: notification.message,
                            params: params
                        });
                    }
                }

                const isScenarioNotice = notification.type === 'scenario_published'
                    || notification.type === 'scenario_created';
                if (!isScenarioNotice || createdAtMs <= lastNotificationCheck) continue;

                const meta = notification.metadata && typeof notification.metadata === 'object' ? notification.metadata : {};
                const params = Array.isArray(notification.params) ? notification.params : [];
                const scenarioName = (meta.scenario_name || params[0] || extractScenarioNameFromNotification(notification) || '').trim();
                const scenarioId = meta.scenario_id || params[1];
                const queued = getScenarioQueuedState();
                if (!queued) continue;

                const idMatch = scenarioId && String(queued.scenarioId) === String(scenarioId);
                const nameMatch = scenarioName && queued.title && String(scenarioName).toLowerCase() === String(queued.title).toLowerCase();
                if (!(idMatch || nameMatch)) continue;

                const notificationId = notification.notification_id || notification.id || null;
                if (!notificationId) continue;
                try {
                    await postScenarioNotification('/notifications/read', token, { notification_id: notificationId });
                } catch { }
            }

            // Advance watermark using the newest server timestamp seen (handles clock skew).
            lastNotificationCheck = newestSeen;
        } catch (error) {
            console.warn('[Scenario] Error checking for sprite notifications:', error);
        }
    };

    const NOTIFICATION_POLL_MS = 10000;
    const scheduleNextNotificationPoll = () => {
        setTimeout(async () => {
            await checkForSpriteNotifications().catch(() => {});
            scheduleNextNotificationPoll();
        }, NOTIFICATION_POLL_MS);
    };
    scheduleNextNotificationPoll();

    // Also check when page becomes visible
    document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
            setTimeout(checkForSpriteNotifications, 1000);
        }
    });

    // Populate outfit dropdowns for all character slots
    await populateRoles();

    applyTranslations('create-scenario', getCurrentLanguage());
    reindexGreetingIntentRows();

    // Attach lightweight sprite preview handlers (no dropdown population)
    const attachSpritePreviewHandlers = () => {
        const updateStatusIconForSlot = (slot) => {
            const charSel = document.getElementById(`crew-char-${slot}`);
            if (!charSel) {
                return;
            }

            const val = charSel.value;

            if (!val || val === '') {
                // No character selected - hide icon or set to neutral state
                setCharStatusIcon(slot, false);
                return;
            }

            // Real character ID - always show as exists
            setCharStatusIcon(slot, 'exists');
        };

        for (let i = 1; i <= 3; i++) {
            const charSel = document.getElementById(`crew-char-${i}`);
            const roleSel = document.getElementById(`crew-role-${i}`);

            if (charSel) {
                charSel.addEventListener('change', async () => {
                    if (isClearingScenarioDraft || isResettingScenarioUI || isReorderingCastSlots) return;
                    const previous = lastCrewBySlot[i] || { id: '', name: '' };
                    const nextId = charSel.value || '';
                    const nextName = charSel.options?.[charSel.selectedIndex]?.textContent?.trim() || '';
                    handleCrewIdentityChange(previous.id, previous.name, nextId, nextName);
                    lastCrewBySlot[i] = { id: nextId, name: nextName };
                    isApplyingCastCharacterChange = true;
                    try {
                        await updateRoleDropdown(i, { forceDefault: true });
                        updateStatusIconForSlot(i);
                        if (!isApplyingAiCastBatch) reorderCastSlotsByCharacterId();
                    } finally {
                        isApplyingCastCharacterChange = false;
                    }
                    if (!isApplyingAiCastBatch) refreshVisibleCastPreviews();
                });
            }
            if (roleSel) roleSel.addEventListener('change', () => {
                if (isClearingScenarioDraft || isResettingScenarioUI || isReorderingCastSlots || isApplyingCastCharacterChange) return;
                updatePreviewForSlot(i);
            });

            // Initial attempt
            updatePreviewForSlot(i);
            updateStatusIconForSlot(i);
        }
    };
    attachSpritePreviewHandlers();
    window.setScenarioGreetingIntentsFromDraft = setGreetingIntentsFromDraft;
    window.setCrewCharacterSlot = setCrewCharacterSlot;
    window.getCharIdentitySnapshot = getCharIdentitySnapshot;
    window.applyAiCharTokensToNames = applyAiCharTokensToNames;
    window.rewriteAiCharTokensToNames = rewriteAiCharTokensToNames;
    window.resolveFilledIdentityTokensInForm = resolveFilledIdentityTokensInForm;
    window.resetCrewIdentities = () => {
        Object.keys(identityByCharId).forEach((key) => delete identityByCharId[key]);
    };
    window.rebuildCrewIdentitiesFromSlots = rebuildIdentitiesFromSlotsFirstFree;
    window.setScenarioAiCastBatch = (isBatch) => {
        isApplyingAiCastBatch = !!isBatch;
        if (!isBatch) {
            rebuildIdentitiesFromSlotsFirstFree();
        }
    };
    window.refreshVisibleCastPreviews = refreshVisibleCastPreviews;

    const addGreetingBtn = document.getElementById('add-greeting-intent');
    if (addGreetingBtn && !addGreetingBtn.dataset.bound) {
        addGreetingBtn.dataset.bound = 'true';
        addGreetingBtn.addEventListener('click', () => addGreetingIntentRow());
        addGreetingBtn.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                addGreetingIntentRow();
            }
        });
    }
    document.addEventListener('languageSwitched', () => {
        reindexGreetingIntentRows();
    });
    document.getElementById('num-characters')?.addEventListener('change', () => {
        syncGreetingIntentSlotVisibility();
        clearHiddenCrewSlots();
        refreshLastCrewBySlot();
    });
    document.getElementById('generate-bg-1')?.addEventListener('change', () => updateCastPartyBackground());
    document.getElementById('time-of-day-1')?.addEventListener('change', () => updateCastPartyBackground());

    // Attach swipe support and arrow handlers for character preview cards
    const attachPreviewSwipeAndArrowHandlers = () => {
        const isSearchDropdownOpen = (selectId) =>
            document.getElementById(`${selectId}-search-trigger`)?.getAttribute('aria-expanded') === 'true';

        const getSelectableOptions = (selectEl) =>
            Array.from(selectEl?.options || []).filter(opt => opt && !opt.disabled && opt.value);

        const cycleSelect = (selectEl, direction) => {
            if (!selectEl || !direction) return;
            const options = getSelectableOptions(selectEl);
            if (!options.length) return;

            const currentValue = selectEl.value;
            let currentIndex = options.findIndex(opt => opt.value === currentValue);
            if (currentIndex < 0) currentIndex = 0;

            const nextIndex = (currentIndex + direction + options.length) % options.length;
            const nextValue = options[nextIndex]?.value;
            if (!nextValue || nextValue === selectEl.value) return;

            selectEl.value = nextValue;
            selectEl.dispatchEvent(new Event('change'));
        };

        const updateArrowVisibility = (slot) => {
            const roleSel = document.getElementById(`crew-role-${slot}`);
            const leftArrow = document.getElementById(`char-${slot}-arrow-left`);
            const rightArrow = document.getElementById(`char-${slot}-arrow-right`);

            const options = getSelectableOptions(roleSel);
            const show = options.length > 1;

            if (leftArrow) leftArrow.style.display = show ? 'flex' : 'none';
            if (rightArrow) rightArrow.style.display = show ? 'flex' : 'none';
        };

        for (let i = 1; i <= 3; i++) {
            const previewCard = document.getElementById(`character-${i}-preview`);
            const roleSel = document.getElementById(`crew-role-${i}`);
            const leftArrow = document.getElementById(`char-${i}-arrow-left`);
            const rightArrow = document.getElementById(`char-${i}-arrow-right`);

            // Add swipe support to preview card
            if (previewCard && !previewCard.dataset.swipeAttached) {
                previewCard.dataset.swipeAttached = 'true';
                addSwipeSupport(previewCard, (direction) => {
                    if (isSearchDropdownOpen(`crew-role-${i}`)) return;
                    cycleSelect(roleSel, direction === 'left' ? 1 : -1);
                });
            }

            // Attach arrow click handlers
            if (leftArrow && !leftArrow.dataset.arrowAttached) {
                leftArrow.dataset.arrowAttached = 'true';
                leftArrow.addEventListener('click', () => cycleSelect(roleSel, -1));
                leftArrow.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        cycleSelect(roleSel, -1);
                    }
                });
            }

            if (rightArrow && !rightArrow.dataset.arrowAttached) {
                rightArrow.dataset.arrowAttached = 'true';
                rightArrow.addEventListener('click', () => cycleSelect(roleSel, 1));
                rightArrow.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        cycleSelect(roleSel, 1);
                    }
                });
            }

            // Update arrow visibility when role dropdown changes
            if (roleSel && !roleSel.dataset.arrowVisibilityAttached) {
                roleSel.dataset.arrowVisibilityAttached = 'true';
                roleSel.addEventListener('change', () => updateArrowVisibility(i));
                // Also update visibility when dropdown is populated (observer)
                const observer = new MutationObserver(() => updateArrowVisibility(i));
                observer.observe(roleSel, { childList: true });
            }

            // Initial arrow visibility
            updateArrowVisibility(i);
        }
    };
    attachPreviewSwipeAndArrowHandlers();

    // Add background dropdown change handler for synthetic backgrounds
    const bgSelect = document.getElementById('generate-bg-1');
    if (bgSelect && !bgSelect.dataset.syntheticHandlerAttached) {
        bgSelect.addEventListener('change', function (e) {
            const val = this.value;

            if (val && val.startsWith('new-bg-')) {
                // Initialize cache if needed
                window.syntheticBackgroundPreviews = window.syntheticBackgroundPreviews || {};

                // Get current time of day to build cache key
                const timeOfDaySelect = document.getElementById('time-of-day-1');
                const timeOfDay = timeOfDaySelect ? timeOfDaySelect.value : '0.1';
                const cacheKey = `${val}.${timeOfDay}`;

                const cached = window.syntheticBackgroundPreviews[cacheKey];
                const prevDiv = document.getElementById('bg-preview-1');


                if (cached && prevDiv) {

                    setBg(prevDiv, cached);

                    // Get references to custom background controls
                    const nameInput = document.getElementById('custom-bg-name');
                    const textarea = document.getElementById('custom-bg-textarea');
                    const genBtn = document.getElementById('custom-bg-generate-btn');

                    // Don't auto-show controls when selecting existing synthetic backgrounds
                    // User must click "Create" button to toggle visibility

                    // Populate name from dropdown if empty
                    if (nameInput && !nameInput.value.trim()) {
                        const selectedOption = bgSelect.selectedOptions && bgSelect.selectedOptions[0];
                        if (selectedOption && selectedOption.textContent) {
                            nameInput.value = selectedOption.textContent.replace(/\s*\(New\)$/, '').trim();
                        }
                    }

                    // Populate textarea with cached prompt for this variant
                    if (textarea && window.syntheticBackgroundPrompts) {
                        const cachedPrompt = window.syntheticBackgroundPrompts[cacheKey];
                        if (cachedPrompt) {
                            textarea.value = cachedPrompt;
                        } else {
                            // Fallback: use any prompt for this background ID
                            const anyKey = Object.keys(window.syntheticBackgroundPrompts).find(k => k.startsWith(`${val}.`));
                            if (anyKey) {
                                textarea.value = window.syntheticBackgroundPrompts[anyKey];
                            }
                        }
                    }

                    e.stopImmediatePropagation(); // prevent other handlers from trying CDN
                } else {
                    // No cache found - let the HTML handler deal with generation
                }
            }
        }, true); // use capture to run before other handlers
        bgSelect.dataset.syntheticHandlerAttached = 'true';
        bgSelect.addEventListener('change', () => updateCastPartyBackground());
    }

    // Save on changes
    if (formElement) {
        const saveDraftDebounced = debounce(saveDraft, 500);
        formElement.addEventListener('input', saveDraftDebounced);
        formElement.addEventListener('change', saveDraftDebounced);
        // Ensure initial state is persisted at least once
        await saveDraft();
    }

    // Clear button handler
    const clearBtn = document.getElementById('clear-draft-btn');
    if (clearBtn) {
        clearBtn.addEventListener('click', async (e) => {
            e.preventDefault();

            if (isClearingScenarioDraft) return;
            isClearingScenarioDraft = true;

            const originalText = clearBtn.innerHTML;
            clearBtn.disabled = true;
            // Let the browser paint the disabled state before any heavy work.
            await new Promise(resolve => requestAnimationFrame(resolve));

            try {
                const existing = storage.getJSON(DRAFT_KEY);

                const previewIdsToDelete = (() => {
                    const ids = new Set();
                    const maybeAdd = (ref) => {
                        if (typeof ref !== 'string') return;
                        if (!ref.startsWith('db:')) return;
                        const id = ref.slice(3);
                        if (id) ids.add(id);
                    };

                    if (existing?.__previews) {
                        maybeAdd(existing.__previews.banner);
                        maybeAdd(existing.__previews.background);

                        const chars = existing.__previews.characters;
                        if (Array.isArray(chars)) {
                            chars.forEach(maybeAdd);
                        } else if (chars && typeof chars === 'object') {
                            Object.values(chars).forEach(maybeAdd);
                        }
                    }

                    // Synthetic background variants live outside __previews (and __previews.background is often null)
                    if (existing?.__backgroundVariants && typeof existing.__backgroundVariants === 'object') {
                        Object.values(existing.__backgroundVariants).forEach(maybeAdd);
                    }

                    return Array.from(ids);
                })();

                storage.remove(DRAFT_KEY);
                // Also reset strict wizard progress (step + completion)
                storage.remove(SCENARIO_WIZARD_PROGRESS_KEY);
                resetScenarioWizardProgress();
                clearScenarioQueuedTimers();
                clearScenarioQueuedState();
                document.getElementById('scenario-queued-inline-card')?.classList.add('is-hidden');
                clearOwnedScenarioEditMode();

                isResettingScenarioUI = true;
                Object.keys(identityByCharId).forEach((key) => delete identityByCharId[key]);
                refreshLastCrewBySlot();
                if (formElement) {
                    formElement.reset();
                    refreshScenarioCounters();

                    // Re-enable any disabled options in selects
                    formElement.querySelectorAll('select option[disabled]')
                        .forEach(opt => opt.disabled = false);
                }

                // Reset visibility UI (form reset doesn't restore button classes)
                const publicBtn = document.getElementById('public-btn');
                const privateBtn = document.getElementById('private-btn');
                const visibilityHidden = document.getElementById('scenario-visibility');
                if (publicBtn && privateBtn) {
                    publicBtn.classList.add('active');
                    privateBtn.classList.remove('active');
                    publicBtn.setAttribute('aria-pressed', 'true');
                    privateBtn.setAttribute('aria-pressed', 'false');
                }
                if (visibilityHidden) visibilityHidden.value = 'public';

                resetPreviewSurface(document.getElementById('banner-image-preview'));
                bannerUploadSource = 'prompt';
                resetPreviewSurface(document.getElementById('bg-preview-1'));

                // Clear character previews and status icons
                for (let i = 1; i <= 3; i++) {
                    resetCastPreview(document.getElementById(`character-${i}-preview`));
                    setCharStatusIcon(i, false);
                }

                // Hide char 2/3 sections & reset dropdown value
                const numCharsSelect = document.getElementById('num-characters');
                if (numCharsSelect) {
                    numCharsSelect.value = '1';
                    numCharsSelect.dispatchEvent(new Event('change'));
                }

                // Remove synthetic background options (new-bg-*) from select
                const removeSynthetic = (sel, prefix) => {
                    const el = document.getElementById(sel);
                    if (!el) return;
                    Array.from(el.options)
                        .filter(o => o.value && o.value.startsWith(prefix))
                        .forEach(o => o.remove());
                    el.selectedIndex = 0; // reset to placeholder
                };

                removeSynthetic('generate-bg-1', 'new-bg-');

                // Also clear role additional synthetic outfit options
                ['crew-role-1', 'crew-role-2', 'crew-role-3']
                    .forEach(id => removeSynthetic(id, 'new-'));

                // Ensure custom searchable dropdown UI matches the underlying selects.
                ['crew-char-1', 'crew-char-2', 'crew-char-3', 'crew-role-1', 'crew-role-2', 'crew-role-3']
                    .forEach(id => ensureSearchableSelect(id));

                // Clear tag picker selection
                try {
                    const { clearScenarioTags } = await import('../tags/scenarioTags.js');
                    clearScenarioTags();
                } catch (e) {
                    console.warn('[Scenario] Failed to clear scenario tags', e);
                }

                // Clear synthetic background caches
                window.syntheticBackgroundPreviews = {};
                window.syntheticBackgroundPrompts = {};
                ['custom-bg-name', 'custom-bg-textarea', 'custom-bg-generate-btn'].forEach((id) => {
                    const el = document.getElementById(id);
                    if (!el) return;
                    el.classList.add('is-hidden');
                    if ('value' in el) el.value = '';
                });

                // Return the user to the first step in the wizard
                setScenarioStep(0);

                // Clean up large previews stored in IndexedDB off the critical path.
                if (previewIdsToDelete.length) {
                    setTimeout(() => {
                        (async () => {
                            for (const id of previewIdsToDelete) {
                                try {
                                    await deletePreview(id);
                                } catch (err) {
                                    console.warn('[Scenario] Failed to delete preview (continuing):', id, err);
                                }
                            }
                        })();
                    }, 0);
                }

                showToast('Draft cleared', 'trash');

            } catch (error) {
                console.error('[Scenario] Error clearing draft:', error);
                showToast('Error clearing draft: ' + error.message, 'exclamation-triangle');
            } finally {
                isResettingScenarioUI = false;
                isClearingScenarioDraft = false;
                clearBtn.disabled = false;
                clearBtn.innerHTML = originalText;
            }
        });
    } else {
        console.warn('[Scenario] Clear button not found in DOM');
    }

    // Set up banner generate button handler
    initStandalonePromptHelpers({
        textareaId: 'adventure-banner-prompt',
        helperRootId: 'prompt-helper-banner',
        pillsRootId: 'prompt-helper-pills-banner',
        getCharacters: () => getScenarioCharactersForPicker(),
        promptRefs: bannerPromptRefs,
        onBareDefaultOutfit: () => {
            showWarning('The default outfit only works as {character:default}.', { icon: 'user', duration: 4000 });
        },
        autocomplete: {
            suggestionType: 'bannerPrompt',
            options: {
                isLinkedBraceToken: (name) => isLinkedBraceToken(bannerPromptRefs, name),
            },
        },
    });

    const generateBannerBtn = document.getElementById('generate-banner-btn');
    if (generateBannerBtn) {
        generateBannerBtn.addEventListener('click', async () => {
            const prompt = document.getElementById('adventure-banner-prompt')?.value?.trim();
            if (!prompt) {
                showWarning('Please enter a banner prompt first.', { icon: 'edit', duration: 4000 });
                return;
            }
            const nsfwBlock = getBlockedNsfwPromptMessage(prompt);
            if (nsfwBlock) {
                showToast(nsfwBlock, 'exclamation-triangle', { duration: 0, closable: true });
                return;
            }
            const unrecognizedBlock = await getBlockedUnrecognizedPromptMessage(prompt, {
                isLinkedBraceToken: (name) => isLinkedBraceToken(bannerPromptRefs, name),
            });
            if (unrecognizedBlock) {
                showToast(unrecognizedBlock, 'exclamation-triangle', { duration: 0, closable: true });
                return;
            }

            try {
                if (!(await requireScenarioAuth())) return;

                // Set loading states
                setLoadingState('generate-banner-btn', true);
                setPreviewLoadingState('banner-image-preview', true);

                // Get banner type
                const bannerType = document.getElementById('ai-banner-type')?.value || 'image';

                const quota = getStoredQuota();
                const previewMode = quota && Number(quota.fast_remaining) <= 0 ? 'slow' : 'fast';
                await generateImage(prompt, 'banner', 'banner-image-preview', bannerType, null, null, previewMode, 'public', 'anime', promptRefsMatchingPrompt(bannerPromptRefs, prompt));
                bannerUploadSource = 'prompt';
                hideBannerCropPanel();
                // Persist the newly generated banner preview immediately so refresh doesn't lose it.
                await saveDraft();

            } catch (error) {
                console.error('Error generating banner:', error);
                showError('Failed to generate banner: ' + error.message, { icon: 'exclamation-circle', duration: 5000 });
            } finally {
                setLoadingState('generate-banner-btn', false);
                setPreviewLoadingState('banner-image-preview', false);
            }
        });
    } else {
        console.warn('[Scenario] Banner generate button not found in DOM');
    }

    const bannerCropPanel = document.getElementById('banner-crop-panel');
    const bannerCropViewport = document.getElementById('banner-crop-viewport');
    const bannerCropImage = document.getElementById('banner-crop-image');
    const bannerCropZoom = document.getElementById('banner-crop-zoom');
    const bannerUploadInput = document.getElementById('banner-upload-input');
    if (isSfwSite() && bannerUploadInput && !document.getElementById('sfw-banner-moderation-note')) {
        const note = document.createElement('p');
        note.id = 'sfw-banner-moderation-note';
        note.className = 'media-crop-hint';
        note.textContent = 'Uploading a banner uses 1 credit for the SFW moderation check.';
        bannerUploadInput.before(note);
    }
    const bannerCropApply = document.getElementById('banner-crop-apply');
    const bannerCropCancel = document.getElementById('banner-crop-cancel');
    const bannerCrop = bindMediaCropPanel({
        viewportEl: bannerCropViewport,
        imageEl: bannerCropImage,
        zoomEl: bannerCropZoom,
        outputWidth: BANNER_CROP_OUTPUT_PX,
        outputHeight: BANNER_CROP_OUTPUT_PX,
        mimeType: 'image/webp',
        quality: 0.9
    });

    const hideBannerCropPanel = () => {
        bannerCrop.destroy();
        bannerCropPanel?.classList.add('is-hidden');
        if (bannerUploadInput) bannerUploadInput.value = '';
    };

    const showBannerCropError = (key, fallback, vars = {}) => {
        const lang = getTranslationLanguage();
        const t = window.translations?.[lang]?.['create-scenario'] ?? window.translations?.en?.['create-scenario'] ?? {};
        const tpl = getByPath(t, key) || fallback;
        showError(interpolate(tpl, vars), { icon: 'exclamation-triangle', duration: 5000 });
    };

    if (bannerUploadInput) {
        bannerUploadInput.addEventListener('change', async (event) => {
            const file = event.target.files?.[0];
            if (!file) return;
            if (!isAllowedBannerUploadType(file.type)) {
                showBannerCropError('steps.banner.upload_unsupported', 'Use a JPEG, PNG, or WebP image.');
                bannerUploadInput.value = '';
                return;
            }
            if (file.size > MAX_SOURCE_UPLOAD_BYTES) {
                showBannerCropError('steps.banner.upload_too_large', 'Image must be {maxMb} MB or smaller.', { maxMb: SOURCE_MAX_MB });
                bannerUploadInput.value = '';
                return;
            }
            try {
                const dataUrl = await new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onload = () => resolve(String(reader.result || ''));
                    reader.onerror = () => reject(new Error('Failed to read file'));
                    reader.readAsDataURL(file);
                });
                bannerCropPanel?.classList.remove('is-hidden');
                await bannerCrop.load(dataUrl);
            } catch (error) {
                console.error('Banner crop load failed:', error);
                showBannerCropError('steps.banner.upload_unsupported', 'Use a JPEG, PNG, or WebP image.');
                hideBannerCropPanel();
            }
        });
    }

    bannerCropApply?.addEventListener('click', async () => {
        if (!bannerCrop.active) return;
        try {
            const dataUrl = await bannerCrop.exportDataUrl();
            if (getDataUrlDecodedByteLength(dataUrl) > MAX_BANNER_OUTPUT_BYTES) {
                showBannerCropError('steps.banner.output_too_large', 'Cropped image must be {maxKb} KB or smaller.', { maxKb: BANNER_OUTPUT_MAX_KB });
                return;
            }
            const promptEl = document.getElementById('adventure-banner-prompt');
            if (promptEl) promptEl.value = '';
            refreshScenarioCounters();
            bannerUploadSource = 'upload';
            setBg(document.getElementById('banner-image-preview'), dataUrl);
            hideBannerCropPanel();
            await saveDraft();
        } catch (error) {
            console.error('Banner crop export failed:', error);
            showError('Failed to apply cropped image.', { icon: 'exclamation-triangle', duration: 5000 });
        }
    });

    bannerCropCancel?.addEventListener('click', () => {
        hideBannerCropPanel();
    });

    // Update banner button text to reflect remaining image generation quota
    const updateBannerBtnFromQuota = (quota) => {
        const btn = document.getElementById('generate-banner-btn');
        if (!btn || btn.disabled) return;
        if (!quota) {
            // Reset to default text (translation system will handle it)
            btn.innerHTML = '<i class="fas fa-magic"></i> <span data-translate="steps.setting.generate">Generate</span>';
            return;
        }
        const lang = getTranslationLanguage();
        const t = window.translations?.[lang]?.['create-scenario'] ?? window.translations?.en?.['create-scenario'] ?? {};
        if (quota.fast_remaining > 0) {
            const tpl = getByPath(t, 'steps.banner.generate_fast_remaining') || '{{count}}/{{total}} free fast left';
            btn.innerHTML = `<i class="fas fa-magic"></i> ${tpl.replace('{{count}}', quota.fast_remaining).replace('{{total}}', quota.fast_total)}`;
        } else if (quota.slow_remaining > 5) {
            const label = getByPath(t, 'steps.banner.generate_slow_remaining') || 'Free slow preview';
            btn.innerHTML = `<i class="fas fa-clock"></i> ${label}`;
        } else if (quota.slow_remaining >= 1) {
            const tpl = getByPath(t, 'steps.banner.generate_slow_remaining_count') || '{{count}}/{{total}} free slow left';
            btn.innerHTML = `<i class="fas fa-clock"></i> ${tpl.replace('{{count}}', quota.slow_remaining).replace('{{total}}', quota.slow_total)}`;
        } else {
            const cost = computeStandaloneImageCost({ style: 'anime', loraCount: 0, width: 1024, height: 1024 });
            const tpl = getByPath(t, 'steps.banner.generate_slow_credits') || 'Generate · {{cost}} credits';
            btn.innerHTML = `<i class="fas fa-clock"></i> ${tpl.replace('{{cost}}', cost)}`;
            btn.disabled = false;
        }
    };

    document.addEventListener('genQuotaUpdated', (e) => updateBannerBtnFromQuota(e.detail));

    updateBannerBtnFromQuota(getStoredQuota());
    loadCreditPricing()
        .then(() => {
            updateBannerBtnFromQuota(getStoredQuota());
            refreshScenarioCreditInfo(false);
        })
        .catch(() => {});
    refreshScenarioCreditInfo(false);
    resumePendingGeneration('banner-image-preview').catch(() => {});

    mountOwnedItemEditPicker({
        root: document.getElementById('owned-scenario-edit-panel'),
        kind: 'scenario',
        titleFallback: 'Or edit a scenario you previously created',
        validateFallback: 'Edit this scenario',
        fetchList: fetchOwnedScenariosForEdit,
        fetchItem: fetchOwnedScenarioForEdit,
        onValidate: applyOwnedScenarioToForm,
    });
}

export async function handleScenarioFormSubmission(e) {
    e.preventDefault();
    if (!(await requireScenarioAuth())) return;
    if (isSubmitting) return;

    if (!isSfwSite() && !ownedScenarioEditId) {
        const creditResult = refreshScenarioCreditInfo(false);
        if (creditResult && creditResult.has_enough_credits === false) {
            showToast(`Insufficient credits. Required: ${CREATION_FLAT_CREDITS}, available: ${creditResult.available_credits}.`, 'exclamation-triangle');
            return;
        }
    }

    const requiredResult = validateRequiredFields('scenario-form', REQUIRED_FIELDS, { showErrors: false });
    if (!requiredResult.valid) {
        const firstMissingId = REQUIRED_FIELDS.find((id) => {
            const el = document.getElementById(id);
            return el && !(el.value || '').trim();
        });
        const step = firstMissingId ? getStepForFieldId(firstMissingId) : 1;
        setScenarioStepUnsafe(step);
        const lang = getCurrentLanguage();
        const t = (typeof window !== 'undefined' && window.translations)?.[lang]?.['create-scenario'] ?? (typeof window !== 'undefined' && window.translations)?.en?.['create-scenario'] ?? {};
        const msg = getByPath(t, 'common.fill_required_fields') || 'Please fill out all required fields.';
        showToast(msg, 'exclamation-triangle');
        return;
    }

    // Collect form data for validation
    const formData = {};
    /** @type {HTMLFormElement|null} */
    const form = /** @type {any} */ (document.getElementById('scenario-form'));
    if (form) {
        const formDataObj = new FormData(form);
        for (const [key, value] of formDataObj.entries()) {
            formData[key] = value;
        }
        // Add character goals manually since they might not be in FormData
        for (let i = 1; i <= 3; i++) {
            // message-* fields removed
        }
    }

    // Check field length limits
    const lengthValidation = validateScenarioFieldLengths(formData);
    if (!lengthValidation.isValid) {
        console.warn('[Scenario] Validation failed: field too long', lengthValidation.field);
        setScenarioStepUnsafe(getStepForFieldId(lengthValidation.field));
        showToast(lengthValidation.message, 'exclamation-triangle');
        const fieldElement = document.getElementById(lengthValidation.field);
        if (fieldElement) {
            fieldElement.focus();
            fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
    }

    const numChars = parseInt(document.getElementById('num-characters')?.value || '1', 10) || 1;
    const title = document.getElementById('adventure-title')?.value?.trim() || '';
    const scenarioDescription = document.getElementById('scenario-description')?.value?.trim() || '';
    const bannerPrompt = document.getElementById('adventure-banner-prompt')?.value?.trim() || '';
    const locationId = document.getElementById('generate-bg-1')?.value || '';
    const tags = getScenarioTags();
    const nsfwTagBlock = getBlockedNsfwScenarioTagMessage(tags);
    if (nsfwTagBlock) {
        showToast(nsfwTagBlock, 'exclamation-triangle');
        return;
    }
    const nsfwBlock = getBlockedNsfwPromptMessage([title, scenarioDescription, bannerPrompt].join(', '));
    if (nsfwBlock) {
        showToast(nsfwBlock, 'exclamation-triangle', { duration: 0, closable: true });
        return;
    }
    const unrecognizedBlock = await getBlockedUnrecognizedPromptMessage(bannerPrompt, {
        isLinkedBraceToken: (name) => isLinkedBraceToken(bannerPromptRefs, name),
    });
    if (unrecognizedBlock) {
        showToast(unrecognizedBlock, 'exclamation-triangle', { duration: 0, closable: true });
        return;
    }
    const visibilityValue = document.getElementById('scenario-visibility')?.value || '';
    const isPrivate = visibilityValue === 'private' || (document.getElementById('private-btn')?.classList?.contains('active') || false);

    if (!title || !scenarioDescription || !locationId) {
        const firstMissingStep = !title || !scenarioDescription ? 1 : 2;
        setScenarioStepUnsafe(firstMissingStep);
        showToast('Please complete all required fields.', 'exclamation-triangle');
        return;
    }

    if (!hasBannerPreviewMedia()) {
        setScenarioStepUnsafe(4);
        showToast('Please generate or upload a banner image.', 'exclamation-triangle');
        return;
    }

    if (tags.length < 1) {
        setScenarioStepUnsafe(1);
        showToast('Please select at least one genre tag.', 'exclamation-triangle');
        return;
    }

    const collectedIntents = collectGreetingIntentsBySlot(numChars);
    if (collectedIntents.error) {
        setScenarioStepUnsafe(3);
        showToast(collectedIntents.error, 'exclamation-triangle');
        return;
    }
    const castLengthError = getScenarioStepLengthError(3);
    if (castLengthError) {
        setScenarioStepUnsafe(3);
        showToast(castLengthError, 'exclamation-triangle');
        return;
    }

    const characters = [];
    for (let i = 1; i <= numChars; i++) {
        const charId = document.getElementById(`crew-char-${i}`)?.value || '';
        const outfitIdRaw = document.getElementById(`crew-role-${i}`)?.value || '';
        let outfitIdNum = parseInt(outfitIdRaw, 10);
        const goal = document.getElementById(`message-${i}`)?.value?.trim() || '';

        // Check character status - simply ensure it's not a red cross
        const statusEl = document.getElementById(`char-status-${i}`);
        const charSel = document.getElementById(`crew-char-${i}`);
        const charName = charSel?.options?.[charSel.selectedIndex]?.textContent || `Character ${i}`;

        // Block submission if character has red cross (not created yet)
        if (statusEl && statusEl.classList.contains('fa-circle-xmark')) {
            setScenarioStepUnsafe(3);
            showToast(`Character "${charName}" has not been created yet. Please click the red ❌ to create the character first.`, 'exclamation-triangle');
            return;
        }

        // If no status icon or character ID is invalid, also block
        if (!charId || !charId.toString().match(/^\d+$/)) {
            setScenarioStepUnsafe(3);
            showToast(`Character ${i} is not properly selected.`, 'exclamation-triangle');
            return;
        }

        // Try to resolve non-numeric outfit values like "default outfit" to a real ID
        let resolvedOutfitId = isNaN(outfitIdNum) ? null : outfitIdNum;
        if (isNaN(outfitIdNum)) {
            const roleSel = document.getElementById(`crew-role-${i}`);
            const selectedText = roleSel?.options?.[roleSel.selectedIndex]?.textContent?.trim() || '';
            const candidate = (outfitIdRaw || selectedText).trim();
            resolvedOutfitId = await resolveOutfitIdByName(candidate);
            if (resolvedOutfitId != null) outfitIdNum = resolvedOutfitId;
        }

        // Validate fields: outfit can be numeric ID (including resolved) or synthetic ID (new-outfit-X)
        const hasValidOutfit = !!outfitIdRaw && (
            !isNaN(outfitIdNum) ||
            (typeof outfitIdRaw === 'string' && outfitIdRaw.startsWith('new-outfit-'))
        );

        if (!charId || !hasValidOutfit) {
            console.warn(`[Scenario] Validation failed for character ${i}:`, {
                charId, outfitIdRaw, outfitIdNum, hasValidOutfit
            });
            setScenarioStepUnsafe(3);
            if (!charId) {
                showToast(`Character ${i} not selected.`, 'exclamation-triangle');
                return;
            }
            showToast(`Character ${i} missing outfit selection.`, 'exclamation-triangle');
            return;
        }

        if (!goal) {
            setScenarioStepUnsafe(3);
            showToast(`Character "${charName}" is missing a goal.`, 'exclamation-triangle');
            return;
        }

        const processedOutfitId = !isNaN(outfitIdNum) ? outfitIdNum : outfitIdRaw;

        characters.push({
            character_id: parseInt(charId, 10),
            outfit_id: processedOutfitId,
            goal,
            greeting_intents: collectedIntents.bySlot[i - 1] || [],
            position: i,
            name: charName
        });
    }

    const letterToSlot = {};
    Object.entries(identityByCharId).forEach(([charId, letter]) => {
        const slot = characters.findIndex((character) => String(character.character_id) === String(charId)) + 1;
        if (slot > 0 && letter) letterToSlot[letter] = slot;
    });

    // Handle synthetic background IDs - don't parse them as integers
    const processedLocationId = locationId && locationId.startsWith('new-bg-')
        ? locationId
        : parseInt(locationId);

    const userId = await getUserId();

    // Start typing animation immediately to show user that processing has begun
    const submitBtn = document.querySelector('#scenario-form button[type="submit"]');
    if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.innerHTML = '<span class="typing-dots">Creating<span>.</span><span>.</span><span>.</span></span>';
    }

    // --------------------------------------------------
    // Ensure we have day/sunset/night variants cached
    // --------------------------------------------------
    let basePrompt = '';
    const taPrompt = document.getElementById('custom-bg-textarea');
    if (taPrompt && taPrompt.value.trim()) basePrompt = taPrompt.value.trim();

    if (!basePrompt && window.syntheticBackgroundPrompts) {
        const anyKey = Object.keys(window.syntheticBackgroundPrompts).find(k => k.startsWith(`${locationId}.`));
        if (anyKey) basePrompt = window.syntheticBackgroundPrompts[anyKey];
    }

    if (locationId && locationId.startsWith('new-bg-') && basePrompt) {
        try {
            await ensureAllBackgroundVariants(locationId, basePrompt);
        } catch (err) {
            console.warn('Failed to auto-generate TOD variants:', err);
        }
    }

    const backgroundVariantImages = {};
    ['0.1', '0.2', '0.3'].forEach(tod => {
        const k = `${locationId}.${tod}`;
        if (window.syntheticBackgroundPreviews && window.syntheticBackgroundPreviews[k]) {
            backgroundVariantImages[tod] = window.syntheticBackgroundPreviews[k];
        }
    });

    // Capture time of day and convert "0.1"/"0.2"/"0.3" to 1/2/3
    const timeOfDayRaw = document.getElementById('time-of-day-1')?.value || '0.1';
    const timeValue = parseInt(timeOfDayRaw.split('.')[1], 10) || 1;

    const scenarioData = tokenizeScenarioPayload({
        name: title,                    // adventure-title
        descr: scenarioDescription, // scenario-description
        goal: '',
        creator_user_id: userId,
        location_id: processedLocationId,
        banner_prompt: bannerUploadSource === 'upload' ? null : bannerPrompt,
        tags,
        nsfwAllowed: getNsfwEnabled(),
        backgroundVariantImages,
        characters,
        is_private: isPrivate,
        time: timeValue                 // time of day: 1=day, 2=sunset, 3=night
    }, { letterToSlot });
    if (ownedScenarioEditId) {
        scenarioData.isUpdate = true;
        scenarioData.scenario_id = ownedScenarioEditId;
    }

    // Add banner image URL if available (for saving to R2)
    const bannerPreviewDiv = document.getElementById('banner-image-preview');
    const getBannerUrlFromDiv = (div) => {
        if (!div) return '';
        const img = div.querySelector('img');
        if (img?.src) return img.src;
        const bg = div.style.backgroundImage || '';
        const m = bg.match(/url\(["']?(.*?)["']?\)/);
        return m ? m[1] : '';
    };

    // Helper to resolve db: URLs from IndexedDB
    const resolvePreviewUrl = async (str) => {
        if (!str) return '';
        if (str.startsWith('db:')) {
            const id = str.slice(3);
            const data = await loadPreview(id);
            return data || '';
        }
        return str;
    };

    let bannerImageUrl = getBannerUrlFromDiv(bannerPreviewDiv);

    // Resolve db: URLs to actual image data
    bannerImageUrl = await resolvePreviewUrl(bannerImageUrl);

    if (bannerImageUrl && (bannerImageUrl.startsWith('http') || bannerImageUrl.startsWith('data:'))) {
        scenarioData.bannerImageUrl = bannerImageUrl;
    }


    // Add background name and description for synthetic backgrounds
    if (locationId && locationId.startsWith('new-bg-')) {
        const bgDropdown = document.getElementById('generate-bg-1');
        const selectedOption = bgDropdown?.options[bgDropdown.selectedIndex];
        const backgroundName = selectedOption?.textContent?.replace(/\s*\(New\)$/, '').trim();

        // Get background description from custom textarea (the prompt used to generate it)
        const customTextarea = document.getElementById('custom-bg-textarea');
        // Try to grab the prompt directly from the textarea. If it is missing (e.g. the user relied on
        // the AI assistant and never created the custom-background controls) fall back to whatever
        // we stored in the syntheticBackgroundPrompts cache so we still persist the prompt.
        const timeOfDayValForPrompt = document.getElementById('time-of-day-1')?.value || '0.1';

        let backgroundDescription = customTextarea?.value?.trim() || '';

        if (!backgroundDescription && window.syntheticBackgroundPrompts) {
            // Prefer an exact TOD match (locationId + "." + timeOfDay). Otherwise grab ANY prompt that
            // starts with the locationId prefix.
            const todVariantKey = `${locationId}.${timeOfDayValForPrompt}`;
            if (window.syntheticBackgroundPrompts[todVariantKey]) {
                backgroundDescription = window.syntheticBackgroundPrompts[todVariantKey];
            } else {
                const anyKey = Object.keys(window.syntheticBackgroundPrompts).find(k => k.startsWith(locationId));
                if (anyKey) backgroundDescription = window.syntheticBackgroundPrompts[anyKey];
            }
        }

        // Get background image URL for saving
        const backgroundPreviewDiv = document.getElementById('bg-preview-1');
        const getUrlFromDiv = (div) => {
            if (!div) return '';
            const img = div.querySelector('img');
            if (img?.src) return img.src;
            const bg = div.style.backgroundImage || '';
            const m = bg.match(/url\(["']?(.*?)["']?\)/);
            return m ? m[1] : '';
        };
        let backgroundImageUrl = getUrlFromDiv(backgroundPreviewDiv);


        // Resolve db: URLs to actual image data
        backgroundImageUrl = await resolvePreviewUrl(backgroundImageUrl);

        if (backgroundName) {
            scenarioData.backgroundName = backgroundName;
            scenarioData.location_name = backgroundName;
            scenarioData.backgroundDescription = backgroundDescription;
            scenarioData.backgroundPrompt = backgroundDescription;

            // Add background image URL if available (for saving to R2)
            if (backgroundImageUrl && (backgroundImageUrl.startsWith('http') || backgroundImageUrl.startsWith('data:'))) {
                scenarioData.backgroundImageUrl = backgroundImageUrl;
            }
        }
    }

    try {
        isSubmitting = true;

        const res = await apiClient.post(`${CREATE_WORKER_URL}/create-scenario`, scenarioData);
        if (!res.success) throw new Error(res.error || 'Failed to create scenario');

        // Clear draft after successful creation
        storage.remove(DRAFT_KEY);

        // Get newly created scenarioId
        const scenarioId = res.scenario_id || res.id || res.data?.scenario_id || res.data?.id;
        applyCreditsRemaining(res.sprite_credits_remaining ?? res.data?.sprite_credits_remaining);
        showToast(
            ownedScenarioEditId
                ? (getByPath(
                    window.translations?.[getTranslationLanguage()]?.['create-scenario']
                    ?? window.translations?.en?.['create-scenario']
                    ?? {},
                    'queued.updated_sub'
                ) || 'Your changes are saved and will be applied to all new messages with this scenario.')
                : 'Scenario created!',
            'check-circle'
        );

        // Reset image generation quota — character creation unlocks a fresh set of requests
        if (!ownedScenarioEditId) {
        try {
            const oauthManager = initOAuthManager();
            const token = await oauthManager.getToken();
            if (token && GENERATION_API_URL) {
                fetch(`${GENERATION_API_URL}/user-gen/reset-quota`, {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` },
                }).catch(() => {});
                try { localStorage.removeItem('user_gen_quota'); } catch {}
                document.dispatchEvent(new CustomEvent('genQuotaUpdated', { detail: null }));
            }
        } catch (e) {
            console.warn('[Scenario] Quota reset failed (non-critical):', e.message);
        }
        }

        // Persist + render queued step (no redirect)
        try {
            // Mark the last form step complete so strict wizard gating allows the queued step
            markScenarioStepComplete(4);
            setScenarioStepUnsafe(5);

            const primaryCharacterId = characters?.[0]?.character_id || '';
            await upsertScenarioQueuedStateFromCompletion({
                scenarioId,
                title,
                bannerUrl: scenarioData.bannerImageUrl || '',
                primaryCharacterId,
                durationMs: 60 * 1000,
                tags: Array.isArray(tags) ? tags : [],
                isPrivate: scenarioData?.is_private === true,
                isNsfw: scenarioIsNsfw({ tags, is_nsfw: scenarioData?.is_nsfw, isN: scenarioData?.isN })
            });
        } catch (e) {
            console.warn('[Scenario] Failed to show queued card after submit', e);
        } finally {
            storage.remove(DRAFT_KEY);
        }

    } catch (error) {
        if (error.message.includes('Authentication required')) {
            sessionStorage.setItem('pending_scenario', JSON.stringify(scenarioData));
            showToast('Please log in to create.', 'exclamation-triangle');
        } else {
            const insufficient = error?.status === 402 || error?.code === 'insufficient_credits' || error?.data?.code === 'insufficient_credits';
            const message = sfwModerationMessage(error)
                || (insufficient && error?.data?.required != null
                    ? `Insufficient credits. Required: ${error.data.required}, available: ${error.data.available ?? 0}.`
                    : error.message);
            showToast(message, 'exclamation-triangle');
        }
    } finally {
        isSubmitting = false;
        const resetBtn = document.querySelector('#scenario-form button[type="submit"]');
        if (resetBtn) {
            resetBtn.disabled = false;
            resetBtn.innerHTML = '<i class="fas fa-dragon"></i> <span data-translate="common.create_adventure">Create Adventure</span>';
        }
    }
}

const formatCountdown = (ms) => {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const SCENARIO_QUEUE_KEY = 'create_scenario_queue_v1';
let __scenarioQueuedIntervals = { tick: null, poll: null };

const safeParseScenarioQueueJson = (raw) => {
    if (!raw || typeof raw !== 'string') return null;
    try { return JSON.parse(raw); } catch { return null; }
};

const clearScenarioQueuedTimers = () => {
    if (__scenarioQueuedIntervals.tick) clearInterval(__scenarioQueuedIntervals.tick);
    if (__scenarioQueuedIntervals.poll) clearInterval(__scenarioQueuedIntervals.poll);
    __scenarioQueuedIntervals.tick = null;
    __scenarioQueuedIntervals.poll = null;
};

const readScenarioQueuedState = () => {
    try {
        const raw = localStorage.getItem(SCENARIO_QUEUE_KEY);
        const parsed = safeParseScenarioQueueJson(raw);
        if (parsed) return parsed;
    } catch { }
    try { return storage.getJSON(SCENARIO_QUEUE_KEY) || null; } catch { return null; }
};

const getScenarioQueuedState = () => {
    const parsed = readScenarioQueuedState();
    if (!parsed?.scenarioId || parsed.ready === true) return parsed;
    const next = { ...parsed, ready: true, readyAt: parsed.readyAt || Date.now() };
    setScenarioQueuedState(next);
    return next;
};

const setScenarioQueuedState = (next) => {
    try { localStorage.setItem(SCENARIO_QUEUE_KEY, JSON.stringify(next)); return; } catch { }
    try { storage.setJSON(SCENARIO_QUEUE_KEY, next); } catch { }
};

const openQueuedScenarioHangout = async (saved) => {
    if (!saved?.scenarioId) return;
    clearScenarioQueuedTimers();
    clearScenarioQueuedState();
    await storageManager.setItem('currentHangoutConfig', {
        scenarioId: String(saved.scenarioId),
        isNewConversation: true,
        isPrivateScenario: saved.isPrivate === true,
        selectedScenario: {
            id: String(saved.scenarioId),
            name: saved.title || '',
            tags: Array.isArray(saved.tags) ? saved.tags : [],
            is_nsfw: saved.isNsfw === true,
            isN: saved.isNsfw ? 1 : 0
        },
        allCharacters: saved.primaryCharacterId ? [{ id: saved.primaryCharacterId, name: '' }] : [],
        entrypoint: 'create_scenario',
        entryMode: 'new',
        greetingVariation: 0,
        greetingVariationCount: 1
    });
    window.location.href = '/hangout.html';
};

const clearScenarioQueuedState = () => {
    try { localStorage.removeItem(SCENARIO_QUEUE_KEY); } catch { }
    try { storage.remove(SCENARIO_QUEUE_KEY); } catch { }
};

const extractScenarioNameFromNotification = (notification) => {
    const direct = (notification?.title || '').trim();
    if (direct) return direct;

    const msg = (notification?.message || '').trim();
    if (!msg) return '';

    // Example: "Your scenario Bond's Gambit has been created!"
    const m = msg.match(/your\s+scenario\s+(.+?)\s+has\s+been\s+created/i);
    if (m && m[1]) return String(m[1]).trim();

    return '';
};

const ensureScenarioQueuedRootVisible = () => {
    const panel = document.getElementById('scenario-queued-panel');
    const root = document.getElementById('scenario-queued-root');
    if (!panel || !root) return null;
    panel.classList.remove('is-hidden');
    return root;
};

const getScenarioQueuedT = () =>
    window.translations?.[getCurrentLanguage()]?.['create-scenario']
    ?? window.translations?.en?.['create-scenario'] ?? {};

const renderScenarioQueuedCardInline = async () => {
    const saved = getScenarioQueuedState();
    if (!saved || !saved.scenarioId || !saved.startedAt) return;

    const root = ensureScenarioQueuedRootVisible();
    if (!root) return;

    const card = document.getElementById('scenario-queued-inline-card');
    if (!card) return;

    if (scenarioStepState.currentStep !== 5) {
        setScenarioStepUnsafe(5);
    }

    card.classList.remove('is-hidden');
    clearScenarioQueuedTimers();

    const t = getScenarioQueuedT();
    const heading = card.querySelector('.scenario-queued-heading');
    const sub = card.querySelector('.scenario-queued-sub');
    const img = card.querySelector('#scenario-queued-media-img');
    const placeholder = card.querySelector('.scenario-queued-media-placeholder');
    const countdown = card.querySelector('.scenario-queued-countdown');
    const startBtn = card.querySelector('.scenario-queued-start-btn');
    if (!heading || !sub || !img || !placeholder || !countdown || !startBtn) return;

    heading.textContent = getByPath(t, 'queued.heading') || 'Your scenario is ready';
    if (saved.title) {
        const tpl = getByPath(t, 'queued.sub_with_title') || '"{{title}}" is ready. You can play it right now!';
        sub.textContent = tpl.replace('{{title}}', saved.title);
    } else {
        sub.textContent = getByPath(t, 'queued.sub_default') || 'Your scenario is ready, you can play it right now!';
    }
    if (saved.isPrivate !== true) {
        const rankingsNote = getByPath(t, 'queued.sub_public_rankings') || 'It will take 1–3 minutes to appear in the rankings.';
        sub.textContent = `${sub.textContent} ${rankingsNote}`;
    }

    if (saved.bannerUrl) {
        img.src = saved.bannerUrl;
        img.alt = saved.title ? `Banner for ${saved.title}` : 'Scenario banner';
        img.classList.remove('is-hidden');
        placeholder.classList.add('is-hidden');
    } else {
        img.removeAttribute('src');
        img.classList.add('is-hidden');
        placeholder.classList.remove('is-hidden');
        placeholder.textContent = getByPath(t, 'queued.media_placeholder') || 'Banner preview unavailable';
    }

    startBtn.textContent = getByPath(t, 'queued.btn_start') || 'Start story';
    startBtn.disabled = true;

    const durationMs = typeof saved.durationMs === 'number' ? saved.durationMs : 60 * 1000;
    let isReady = !!saved.scenarioId;

    const syncStartButton = (leftMs) => {
        const shouldShow = isReady || leftMs <= 0;

        if (!shouldShow) {
            startBtn.classList.add('is-hidden');
            return;
        }

        startBtn.classList.remove('is-hidden');

        if (isReady) {
            startBtn.disabled = false;
            startBtn.textContent = getByPath(t, 'queued.btn_start') || 'Start story';
            return;
        }

        startBtn.disabled = true;
        startBtn.textContent = getByPath(t, 'queued.btn_almost_ready') || 'Almost ready…';
    };

    const updateCountdown = () => {
        if (!isReady) {
            const latest = getScenarioQueuedState();
            if (latest && String(latest.scenarioId) === String(saved.scenarioId) && latest.ready) {
                isReady = true;
            }
        }

        const elapsed = Date.now() - saved.startedAt;
        const left = Math.max(0, durationMs - elapsed);
        if (isReady) {
            // Once ready, stop showing a countdown (button is the primary CTA).
            countdown.classList.add('is-hidden');
            syncStartButton(0);
            clearScenarioQueuedTimers();
            return;
        }

        countdown.classList.remove('is-hidden');
        if (left > 0) {
            const tpl = getByPath(t, 'queued.countdown_ready_in') || 'Ready in {{countdown}}';
            countdown.textContent = tpl.replace('{{countdown}}', formatCountdown(left));
        } else {
            countdown.textContent = getByPath(t, 'queued.countdown_ready_soon') || 'Ready soon';
        }
        syncStartButton(left);
    };
    updateCountdown();
    if (!isReady) {
        __scenarioQueuedIntervals.tick = setInterval(updateCountdown, 1000);
    }

    if (!startBtn.dataset.bound) {
        startBtn.dataset.bound = 'true';
        startBtn.addEventListener('click', (e) => {
        e.preventDefault();
        if (startBtn.disabled) return;
        openQueuedScenarioHangout(saved).catch(() => {});
        });
    }
};

/**
 * Persist scenario queue state (survives reloads).
 * @param {{
 *   scenarioId: string|number,
 *   title?: string,
 *   bannerUrl?: string,
 *   primaryCharacterId?: string|number,
 *   durationMs?: number
 * }=} opts
 */
const upsertScenarioQueuedStateFromCompletion = async (opts = /** @type {any} */ ({})) => {
    const {
        scenarioId,
        title,
        bannerUrl,
        primaryCharacterId,
        durationMs = 60 * 1000,
        tags,
        isPrivate,
        isNsfw
    } = opts || {};

    if (!scenarioId) return;
    const existing = getScenarioQueuedState();

    const next = {
        scenarioId: String(scenarioId),
        title: title || existing?.title || '',
        bannerUrl: bannerUrl || existing?.bannerUrl || '',
        primaryCharacterId: primaryCharacterId || existing?.primaryCharacterId || '',
        tags: Array.isArray(tags) ? tags : (existing?.tags || []),
        isPrivate: typeof isPrivate === 'boolean' ? isPrivate : existing?.isPrivate === true,
        isNsfw: typeof isNsfw === 'boolean' ? isNsfw : existing?.isNsfw === true,
        startedAt: existing?.startedAt || Date.now(),
        durationMs,
        ready: true,
        readyAt: existing?.readyAt || Date.now()
    };

    setScenarioQueuedState(next);
    await renderScenarioQueuedCardInline();
};

async function getUserId() {
    const authToken = await window.tokenManager?.getValidAccessToken?.() ?? null;
    if (!authToken) throw new Error('Authentication required');

    try {
        const payload = JSON.parse(atob(authToken.split('.')[1]));
        if (payload.sub) return payload.sub;
    } catch { }

    const profile = storage.get('user_profile') || {};
    if (profile.id) return profile.id;

    const { user } = await apiClient.get(`${USER_WORKER_URL}/user/profile`);
    if (!user?.id) throw new Error('Authentication required');

    storage.set('user_profile', user);
    return user.id;
}

// ------------------------------------------------------------
// Background helpers – ensure all TOD variants are generated
// ------------------------------------------------------------

/**
 * Ensure day (0.1), sunset (0.2) and night (0.3) variants exist in
 *   window.syntheticBackgroundPreviews for the given synthetic location.
 * If a variant is missing, it renders off–screen using generateBackgroundPreview
 * so we always have three images to upload.
 * @param {string} locationId  e.g. new-bg-haunted-castle
 * @param {string} basePrompt  prompt used to generate the background
 */
async function ensureAllBackgroundVariants(locationId, basePrompt) {
    const TODS = ['0.1', '0.2', '0.3']; // day, sunset, night
    window.syntheticBackgroundPreviews = window.syntheticBackgroundPreviews || {};
    window.syntheticBackgroundPrompts = window.syntheticBackgroundPrompts || {};

    let tmpDiv = document.getElementById('tmp-bg-gen');
    if (!tmpDiv) {
        tmpDiv = document.createElement('div');
        tmpDiv.id = 'tmp-bg-gen';
        tmpDiv.style.cssText = 'display:none;width:600px;height:400px;';
        document.body.appendChild(tmpDiv);
    }

    for (const tod of TODS) {
        const key = `${locationId}.${tod}`;
        if (window.syntheticBackgroundPreviews[key]) continue; // already have it

        // generateBackgroundPreview is globally available from imageGeneration module
        await generateBackgroundPreview(basePrompt, tmpDiv.id, tod);
        /** @type {HTMLImageElement|null} */
        const img = /** @type {any} */ (tmpDiv.querySelector('img'));
        if (img?.src) {
            window.syntheticBackgroundPreviews[key] = img.src;
            window.syntheticBackgroundPrompts[key] = basePrompt;
        }
        tmpDiv.innerHTML = '';
    }
}

